import { semanticColors } from '@aztec-realms/design-tokens';
import type { BuildingView, QualityTier, SettlementViewModel } from '@aztec-realms/shared-types';

import {
  environmentAssetManifest,
  proceduralBuildingManifest,
  resolveBranchOverlay,
  resolveSettlementBuildingSprite,
  resolveSettlementConstructionArt,
  resolveSettlementConstructionAccessory,
  settlementDetailSpriteManifest,
  settlementWaterFoundationManifest,
  type ConstructionMaterialState,
  type ProceduralBuildingAsset,
  type SettlementBuildingSpriteAsset,
} from './asset-manifest';
import { constructionMotionProfile, resolveConstructionStage } from './construction-stage';
import {
  planSettlementConstructionLayout,
  planSettlementFinishingWorksite,
} from './settlement-construction-layout';
import {
  constructionContactArt,
  constructionContactPolygon,
  fitConstructionContact,
} from './settlement-construction-contact';
import {
  settlementBuildingTierScale,
  settlementBuildingGroundAnchor,
  registeredArtworkScale,
} from './settlement-building-art';
import {
  compareSettlementGroundDepth,
  clampSettlementCamera,
  createSettlementWorldFrame,
  createSettlementDistrictFrame,
  SETTLEMENT_REGISTERED_EDITOR_MAX_ZOOM,
  createSettlementPlacementCamera,
  createSettlementEditorCamera,
  initialSettlementCamera,
  projectSettlementGround,
  projectSettlementFootprint,
  settlementTileWidth,
  settlementBuildingBounds,
  settlementBuildingArtScale,
  settlementGridGuideStroke,
  settlementFramingDistricts,
  zoomSettlementCamera,
  unprojectSettlementGround,
  type SettlementCameraView,
  type SettlementGroundProjection,
  type SettlementGridFrame,
  type SettlementGridBounds,
} from './settlement-projection';
import {
  createSettlementScenePlan,
  settlementSceneRevision,
  type SettlementScenePlan,
} from './settlement-scene-plan';
import { settlementIslandCover } from './settlement-island-registration';
import { drawSettlementShoreFeather } from './settlement-shore-feather';
import { observeSettlementHostSize } from './settlement-host-size';
import { destroySettlementGame } from './settlement-game-lifecycle';
import {
  homeTextureVariants,
  HomeTextureUpgradeQueue,
  loadHomeTextureImage,
  nativeHomeTextureKey,
  needsNativeHomeTexture,
} from './home-texture-delivery';
import { requiredSettlementArt, type SettlementRequiredArt } from './settlement-required-art';
import {
  drawSettlementWaterFoundation,
  supportsSettlementWaterFoundation,
  drawSettlementGroundTexture,
} from './settlement-water-foundation';
import {
  settlementExpansionGround,
  settlementPrimaryPreservationOutline,
} from './settlement-expansion-ground';
import type { IslandQuad } from './settlement-island-registration';
import { isSettlementSpriteHit } from './settlement-sprite-hit';
import { planSettlementEditorFocus } from './settlement-editor-focus';
import { planSettlementResidents } from './settlement-resident-plan';
import { planSettlementPublicPaths, type SettlementPublicPath } from './settlement-public-paths';
import { settlementPathMaterial, settlementPathStamps } from './settlement-path-material';
import { planSettlementBridgeMaterial } from './settlement-bridge-material';
import {
  assembleSettlementBridgeStrip,
  rehydrateSettlementBridgeStrip,
  settlementBridgeDeckDelivery,
  settlementGardenBridgeStripId,
} from './settlement-bridge-delivery';
import {
  planSettlementBridgeStructure,
  type SettlementBridgeSide,
} from './settlement-bridge-structure';
import { settlementBridgePostManifest, settlementBridgeRailSource } from './settlement-bridge-art';
import {
  createSettlementIslandBridge,
  SETTLEMENT_BRIDGE_CONTACT_IDENTITY,
} from './settlement-island-bridges';

export interface PlacementPreview {
  /** Controls are outside the canvas, so all of its viewport is available. */
  unobscuredViewport?: boolean;
  buildingId: string;
  x: number;
  y: number;
  valid: boolean;
  orientation?: number;
  footprintWidth?: number;
  footprintHeight?: number;
  districtBounds?: SettlementGridBounds;
}

export type SettlementCameraAction = 'zoom-in' | 'zoom-out' | 'fit';

export interface SettlementRendererOptions {
  signal?: AbortSignal;
  qualityTier: QualityTier;
  reducedMotion: boolean;
  selectedBuildingId?: string;
  placement?: PlacementPreview;
  editing?: boolean;
  onPlacementChanged?: (x: number, y: number) => void;
  onBuildingSelected: (buildingId: string) => void;
  onReady: () => void;
  onArtUnavailable?: () => void;
  onArtLoading?: () => void;
}

export interface SettlementRendererController {
  update: (snapshot: SettlementViewModel, selectedBuildingId?: string) => void;
  setPlacement: (preview?: PlacementPreview) => void;
  setEditing: (editing: boolean) => void;
  commandCamera: (action: SettlementCameraAction) => void;
  destroy: () => void;
}

interface Point {
  x: number;
  y: number;
}

// Keep retained decoration experiments out of runtime loads and required-art checks.
// Real owned farm/garden construction still uses the plantingBeds detail asset.
export const settlementRuntimeDetailAssets = Object.values(settlementDetailSpriteManifest).filter(
  (asset) => asset.role !== 'ambient_vegetation',
);

function color(value: string): number {
  return Number.parseInt(value.slice(1), 16);
}

// Centralized material palette for generated settlement art. Interactive jade and
// gold still derive from the semantic design system, while world surfaces use
// quieter paint values so they read as stone, water, earth and cloth, not debug UI.
const materials = {
  deep: color(semanticColors.backgroundDeep),
  jade: color(semanticColors.jade),
  cloud: color(semanticColors.cloud),
  skyHigh: 0x365f68,
  skyLow: 0xc3a778,
  mountainFar: 0x4e6965,
  mountainNear: 0x314f4c,
  haze: 0xd7d1b7,
  waterDeep: 0x0a3b43,
  waterMid: 0x155761,
  waterLight: 0x76a9a0,
  waterShade: 0x092d34,
  stoneTop: 0xc7ba98,
  stoneLight: 0xd9ccaa,
  stoneSide: 0x82755c,
  stoneDark: 0x4c5148,
  plaster: 0xd8cfb4,
  earth: 0x594b32,
  earthLight: 0x806b3f,
  timber: 0x69482d,
  timberLight: 0x997246,
  thatch: 0xa7834d,
  thatchLight: 0xc5a364,
  obsidian: 0x111a1a,
  obsidianSheen: 0x364b49,
  jadeWeathered: 0x2a776b,
  jadeDeep: 0x124d49,
  goldAged: 0xb78d3e,
  goldGlow: 0xe2c16c,
  maizeLeaf: 0x738249,
  maizeEar: 0xc4a14e,
  foliageDark: 0x234b37,
  foliage: 0x3f7047,
  foliageLight: 0x789457,
  reed: 0x8a8b55,
  textileRust: 0x9d513c,
  textileCream: 0xd2bc89,
  textileIndigo: 0x315e62,
  smoke: 0xb5b6a6,
  skin: 0x9c6746,
};

export async function mountSettlementRenderer(
  parent: HTMLElement,
  initialSnapshot: SettlementViewModel,
  options: SettlementRendererOptions,
): Promise<SettlementRendererController> {
  const PhaserModule = await import('phaser');
  options.signal?.throwIfAborted();
  const Phaser = PhaserModule.default;
  let snapshot = initialSnapshot;
  let selectedBuildingId = options.selectedBuildingId;
  let placement = options.placement;
  let editing = options.editing ?? false;

  class SettlementScene extends Phaser.Scene {
    private world?: InstanceType<typeof Phaser.GameObjects.Container>;
    private placementGuides?: InstanceType<typeof Phaser.GameObjects.Graphics>;
    private cameraView: SettlementCameraView = { zoom: 1.12, offsetX: 0, offsetY: 0 };
    private pointers = new Map<number, Point>();
    private gestureTravel = 0;
    private selectionLayers = new Map<string, InstanceType<typeof Phaser.GameObjects.Graphics>>();
    private buildingAnchors = new Map<string, Point>();
    private visualRevision?: string;
    private occupiedPlots: SettlementGridBounds[] = [];
    private groundDistricts: SettlementGridBounds[] = [];
    private buildingLayers = new Map<string, InstanceType<typeof Phaser.GameObjects.Container>>();
    private touchZones: {
      zone: InstanceType<typeof Phaser.GameObjects.Zone>;
      width: number;
      height: number;
    }[] = [];
    private placementDrag?: { pointerId: number; offset: Point };
    private lastPlacementTile?: Point;
    private placementSourceFootprint?: SettlementGridBounds;
    private placementCameraPending = false;
    private normalCamera?: SettlementCameraView;
    private groundTextures = new Set<string>();
    private buildingSprites = new Map<string, InstanceType<typeof Phaser.GameObjects.Image>>();
    private cropHeights = new WeakMap<InstanceType<typeof Phaser.GameObjects.Image>, number>();
    private bootArt: SettlementRequiredArt[] = [];
    private artLoadPending = false;
    private artLoadFailed = false;
    private failedArtVariants = new Set<string>();
    private nativeArtFallbacks = new Set<string>();
    private preferredArtUrls = new Map<string, string>();
    private responsiveHomes: {
      art: InstanceType<typeof Phaser.GameObjects.Image>;
      asset: { id: string; url: string };
      width: number;
    }[] = [];
    private homeUpgrades?: HomeTextureUpgradeQueue<HTMLImageElement>;
    private publicPaths: SettlementPublicPath[] = [];

    constructor() {
      super('settlement');
    }

    preload() {
      this.load.on('loaderror', (file: { key: string; url: string }) => {
        if (this.preferredArtUrls.get(file.key) === file.url) this.failedArtVariants.add(file.key);
      });
      this.bootArt = requiredSettlementArt(snapshot, placement);
      this.enqueueArt(this.bootArt);
    }

    private enqueueArt(assets: readonly SettlementRequiredArt[]) {
      for (const asset of assets) {
        if (this.textures.exists(asset.id)) continue;
        if (asset.frameWidth && asset.frameHeight)
          this.load.spritesheet(asset.id, asset.url, {
            frameWidth: asset.frameWidth,
            frameHeight: asset.frameHeight,
          });
        else {
          const delivery = homeTextureVariants[asset.url];
          const preferred = delivery?.variant.url ?? asset.url;
          const full = asset.fallbackUrl ?? asset.url;
          if (preferred !== full) this.preferredArtUrls.set(asset.id, preferred);
          const fallback = this.failedArtVariants.has(asset.id);
          if (fallback) this.nativeArtFallbacks.add(asset.id);
          this.load.image(asset.id, fallback ? full : preferred);
        }
      }
    }

    private ensureSceneArt(resume: () => void, notifyReady = true): boolean {
      if (this.artLoadPending || this.artLoadFailed) return false;
      const missing = requiredSettlementArt(snapshot, placement).filter(
        (asset) => !this.textures.exists(asset.id),
      );
      if (!missing.length) return true;
      this.artLoadPending = true;
      this.input.enabled = false;
      this.clearGesture();
      options.onArtLoading?.();
      this.enqueueArt(missing);
      this.load.once('complete', () => {
        if (!this.sys.isActive()) return;
        this.artLoadPending = false;
        const currentIds = new Set(
          requiredSettlementArt(snapshot, placement).map((asset) => asset.id),
        );
        if (
          missing.some(
            (asset) =>
              currentIds.has(asset.id) &&
              !this.textures.exists(asset.id) &&
              this.failedArtVariants.has(asset.id) &&
              !this.nativeArtFallbacks.has(asset.id),
          )
        ) {
          this.ensureSceneArt(resume, notifyReady);
          return;
        }
        if (missing.some((asset) => currentIds.has(asset.id) && !this.textures.exists(asset.id))) {
          this.artLoadFailed = true;
          options.onArtUnavailable?.();
          return;
        }
        this.input.enabled = true;
        resume();
        if (notifyReady && !this.artLoadPending && !this.artLoadFailed)
          this.reportReadyAfterRender();
      });
      this.load.start();
      return false;
    }

    private reportReadyAfterRender() {
      this.game.events.once('postrender', () => {
        if (this.sys.isActive() && !this.artLoadPending && !this.artLoadFailed) options.onReady();
      });
    }

    create() {
      const currentIds = new Set(
        requiredSettlementArt(snapshot, placement).map((asset) => asset.id),
      );
      if (
        this.bootArt.some(
          (asset) =>
            currentIds.has(asset.id) &&
            !this.textures.exists(asset.id) &&
            !this.failedArtVariants.has(asset.id),
        )
      ) {
        this.artLoadFailed = true;
        options.onArtUnavailable?.();
        return;
      }
      if (!this.ensureSceneArt(() => this.create(), false)) return;
      this.homeUpgrades = new HomeTextureUpgradeQueue(loadHomeTextureImage, (key, image) => {
        if (!this.sys.isActive()) return;
        if (!this.textures.exists(key)) this.textures.addImage(key, image);
        this.refreshHomeResolution();
      });
      if (placement || editing) this.preparePlacementCamera();
      this.drawWorld();
      if (!placement && !editing) this.setCameraView(this.initialWorldCamera());
      const resize = () => {
        this.clearGesture();
        this.placementCameraPending = !!placement || editing;
        this.drawWorld();
        // A changed world aspect needs a fresh terrain camera, not the previous pan/zoom.
        // Placement continues to use its separate selected-building focus contract.
        if (!placement && !editing) {
          this.normalCamera = undefined;
          this.setCameraView(this.initialWorldCamera());
        }
      };
      this.scale.on('resize', resize);
      const stopObservingHost = observeSettlementHostSize(
        parent,
        () => ({ width: this.scale.width, height: this.scale.height }),
        // RESIZE mode derives canvas dimensions from parentSize during refresh.
        // Direct resize() is overwritten by that cached parent size after CSS-only changes.
        (width, height) => this.scale.setParentSize(width, height),
      );
      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        stopObservingHost();
        this.scale.off('resize', resize);
        this.game.events.off('blur', this.clearGesture, this);
        this.clearGroundTextures();
        this.homeUpgrades?.dispose();
        this.responsiveHomes = [];
        this.events.off('shutdown', cleanup);
        this.events.off('destroy', cleanup);
      };
      // Game.destroy() destroys scenes directly without emitting their shutdown event.
      this.events.once('shutdown', cleanup);
      this.events.once('destroy', cleanup);
      this.input.on(
        'wheel',
        (
          pointer: InstanceType<typeof Phaser.Input.Pointer>,
          _objects: unknown[],
          _dx: number,
          deltaY: number,
        ) => {
          if (this.placementDrag) return;
          this.setCameraView(
            zoomSettlementCamera(
              this.cameraView,
              this.cameraView.zoom - Math.sign(deltaY) * 0.08,
              pointer,
              this.scale.width,
              this.scale.height,
              this.maximumZoom(),
              this.gridFrame?.registration?.cover,
            ),
          );
        },
      );
      this.input.on('pointerdown', (pointer: InstanceType<typeof Phaser.Input.Pointer>) => {
        if (this.pointers.size === 0) this.gestureTravel = 0;
        this.pointers.set(pointer.id, { x: pointer.x, y: pointer.y });
        if (this.pointers.size > 1) {
          this.gestureTravel = Infinity;
          this.placementDrag = undefined;
        }
        if (this.world) {
          this.tweens.killTweensOf(this.world);
          this.cameraView = {
            zoom: this.world.scaleX,
            offsetX: this.world.x,
            offsetY: this.world.y,
          };
        }
      });
      this.input.on('pointermove', (pointer: InstanceType<typeof Phaser.Input.Pointer>) => {
        const previous = this.pointers.get(pointer.id);
        if (!pointer.isDown || !previous) return;
        const next = { x: pointer.x, y: pointer.y };
        if (this.placementDrag?.pointerId === pointer.id && placement) {
          this.gestureTravel += Math.hypot(next.x - previous.x, next.y - previous.y);
          this.pointers.set(pointer.id, next);
          const tileWidth = settlementTileWidth(this.scale.height);
          const worldPoint = this.pointerInWorld(pointer);
          const grid = unprojectSettlementGround(
            worldPoint.x - this.placementDrag.offset.x,
            worldPoint.y - this.placementDrag.offset.y,
            this.scale.width,
            this.scale.height,
            tileWidth,
            tileWidth / 2,
            this.gridFrame,
          );
          if (grid && this.gestureTravel >= 8) {
            const x = Math.round(grid.x);
            const y = Math.round(grid.y);
            if (x !== this.lastPlacementTile?.x || y !== this.lastPlacementTile?.y) {
              this.lastPlacementTile = { x, y };
              options.onPlacementChanged?.(x, y);
            }
          }
          return;
        }
        const other = [...this.pointers.entries()].find(([id]) => id !== pointer.id)?.[1];
        this.gestureTravel += Math.hypot(next.x - previous.x, next.y - previous.y);
        if (other) {
          const distance = Math.hypot(previous.x - other.x, previous.y - other.y);
          if (distance > 0)
            this.setCameraView(
              zoomSettlementCamera(
                this.cameraView,
                (this.cameraView.zoom * Math.hypot(next.x - other.x, next.y - other.y)) / distance,
                { x: (next.x + other.x) / 2, y: (next.y + other.y) / 2 },
                this.scale.width,
                this.scale.height,
                this.maximumZoom(),
                this.gridFrame?.registration?.cover,
              ),
            );
        } else if (this.gestureTravel >= 8) {
          this.setCameraView({
            ...this.cameraView,
            offsetX: this.cameraView.offsetX + next.x - previous.x,
            offsetY: this.cameraView.offsetY + next.y - previous.y,
          });
        }
        this.pointers.set(pointer.id, next);
      });
      const release = (pointer: InstanceType<typeof Phaser.Input.Pointer>) => {
        this.pointers.delete(pointer.id);
        if (this.placementDrag?.pointerId === pointer.id) this.placementDrag = undefined;
      };
      this.input.on('pointerup', release);
      this.input.on('pointerupoutside', release);
      this.input.on('gameout', () => this.clearGesture());
      this.game.events.on('blur', this.clearGesture, this);
      this.reportReadyAfterRender();
    }

    redraw(nextSnapshot: SettlementViewModel, nextSelectedBuildingId?: string) {
      const snapshotChanged = this.visualRevision !== settlementSceneRevision(nextSnapshot);
      const selectionChanged = selectedBuildingId !== nextSelectedBuildingId;
      const previousDistrict = editing ? JSON.stringify(this.editorDistrict()) : undefined;
      snapshot = nextSnapshot;
      selectedBuildingId = nextSelectedBuildingId;
      if (!this.scene.isActive() || !this.world) return;
      const districtChanged = editing && previousDistrict !== JSON.stringify(this.editorDistrict());
      if (districtChanged) this.placementCameraPending = true;
      if (snapshotChanged || districtChanged) this.drawWorld();
      if (selectionChanged && !placement && !editing) {
        for (const [id, layer] of this.selectionLayers) layer.setVisible(id === selectedBuildingId);
        const anchor = selectedBuildingId
          ? this.buildingAnchors.get(selectedBuildingId)
          : undefined;
        if (anchor)
          this.setCameraView(
            {
              ...this.cameraView,
              offsetX: this.scale.width * 0.5 - anchor.x * this.cameraView.zoom,
              offsetY: this.scale.height * 0.48 - anchor.y * this.cameraView.zoom,
            },
            !options.reducedMotion,
          );
      }
    }

    private clearGesture() {
      this.pointers.clear();
      this.placementDrag = undefined;
      this.lastPlacementTile = undefined;
      this.gestureTravel = Infinity;
    }

    private pointerInWorld(pointer: Point): Point {
      const zoom = this.world?.scaleX ?? this.cameraView.zoom;
      return {
        x: (pointer.x - (this.world?.x ?? this.cameraView.offsetX)) / zoom,
        y: (pointer.y - (this.world?.y ?? this.cameraView.offsetY)) / zoom,
      };
    }

    private buildingBounds(building: BuildingView): SettlementGridBounds {
      return settlementBuildingBounds(
        building,
        proceduralBuildingManifest[building.type].footprint,
      );
    }

    private preparePlacementCamera() {
      const selected = snapshot.buildings.find((building) => building.id === placement?.buildingId);
      this.placementSourceFootprint =
        selected && placement
          ? this.buildingBounds({
              ...selected,
              gridX: placement.x,
              gridY: placement.y,
              orientation: placement.orientation ?? selected.orientation,
              footprintWidth: placement.footprintWidth ?? selected.footprintWidth,
              footprintHeight: placement.footprintHeight ?? selected.footprintHeight,
            })
          : undefined;
      this.placementCameraPending = true;
    }

    private editorDistrict(): SettlementGridBounds | undefined {
      if (placement?.districtBounds) return placement.districtBounds;
      const selected = snapshot.buildings.find((building) => building.id === selectedBuildingId);
      const district = snapshot.districts.find(
        (candidate) => candidate.unlocked && candidate.code === selected?.districtCode,
      );
      return district
        ? { ...district.bounds, maxX: district.bounds.maxX + 1, maxY: district.bounds.maxY + 1 }
        : settlementFramingDistricts(snapshot.districts, [])[0];
    }

    setEditing(next: boolean) {
      if (editing === next) return;
      editing = next;
      if (!this.scene.isActive() || !this.world) return;
      this.clearGesture();
      if (next) {
        this.normalCamera = {
          zoom: this.world.scaleX,
          offsetX: this.world.x,
          offsetY: this.world.y,
        };
        this.placementCameraPending = true;
      } else {
        this.placementCameraPending = false;
        this.setCameraView(this.normalCamera ?? this.initialWorldCamera());
        this.normalCamera = undefined;
      }
      this.drawWorld();
    }

    commandCamera(action: SettlementCameraAction) {
      if (!editing || !this.world) return;
      this.clearGesture();
      const district = this.editorDistrict();
      if (action === 'fit') {
        if (district && this.gridFrame)
          this.setCameraView(
            createSettlementEditorCamera(
              this.gridFrame,
              district,
              this.scale.width,
              this.scale.height,
              placement?.unobscuredViewport,
            ),
          );
        return;
      }
      this.setCameraView(
        zoomSettlementCamera(
          this.cameraView,
          this.cameraView.zoom * (action === 'zoom-in' ? 1.2 : 1 / 1.2),
          { x: this.scale.width * 0.5, y: this.scale.height * 0.35 },
          this.scale.width,
          this.scale.height,
          this.maximumZoom(),
          this.gridFrame?.registration?.cover,
        ),
      );
    }

    setPlacement(next?: PlacementPreview) {
      const previous = placement;
      const previousDistrict = editing ? JSON.stringify(this.editorDistrict()) : undefined;
      const frameChanged =
        previous?.buildingId !== next?.buildingId ||
        JSON.stringify(previous?.districtBounds) !== JSON.stringify(next?.districtBounds);
      placement = next;
      if (!this.scene.isActive() || !this.world) return;
      if (frameChanged) {
        this.clearGesture();
        if (next) {
          if (!previous && !editing) {
            this.normalCamera = {
              zoom: this.world.scaleX,
              offsetX: this.world.x,
              offsetY: this.world.y,
            };
          }
          // A new selection within the SAME district needs focus too. Draft motion
          // uses the incremental branch below and must not chase the pointer.
          this.preparePlacementCamera();
        } else {
          this.placementSourceFootprint = undefined;
          this.placementCameraPending =
            editing && previousDistrict !== JSON.stringify(this.editorDistrict());
          if (!editing) {
            this.setCameraView(this.normalCamera ?? this.cameraView);
            this.normalCamera = undefined;
          }
        }
        this.drawWorld();
      } else if (
        next &&
        (previous?.x !== next.x ||
          previous.y !== next.y ||
          previous.valid !== next.valid ||
          previous.orientation !== next.orientation ||
          previous.footprintWidth !== next.footprintWidth ||
          previous.footprintHeight !== next.footprintHeight)
      ) {
        this.drawPlacementBuilding();
      }
    }

    private drawPlacementBuilding() {
      if (this.artLoadPending || this.artLoadFailed) return;
      if (!this.ensureSceneArt(() => this.drawWorld())) return;
      const preview = placement;
      if (!preview || !this.world) return;
      const building = snapshot.buildings.find((candidate) => candidate.id === preview.buildingId);
      if (!building) return;
      const layer = this.buildingLayers.get(building.id);
      if (!layer) return;
      for (const child of layer.list) this.tweens.killTweensOf(child);
      layer.removeAll(true);
      this.touchZones = this.touchZones.filter(({ zone }) => zone.scene);
      this.responsiveHomes = this.responsiveHomes.filter(({ art }) => art.scene);
      const tileWidth = settlementTileWidth(this.scale.height);
      this.drawBuilding(
        layer,
        {
          ...building,
          gridX: preview.x,
          gridY: preview.y,
          orientation: preview.orientation ?? building.orientation,
          footprintWidth: preview.footprintWidth ?? building.footprintWidth,
          footprintHeight: preview.footprintHeight ?? building.footprintHeight,
        },
        tileWidth,
        tileWidth / 2,
        [],
      );
      this.applyEditorFocus();
      this.world.sort('depth');
      this.resizeTouchZones(this.cameraView.zoom);
      this.refreshHomeResolution();
    }

    private resizeTouchZones(zoom: number) {
      for (const { zone, width, height } of this.touchZones) {
        if (!zone.scene) continue;
        zone.setSize(Math.max(44 / zoom, width), Math.max(44 / zoom, height));
        const hit = zone.input?.hitArea;
        if (hit) {
          hit.width = zone.width;
          hit.height = zone.height;
        }
      }
    }

    private setCameraView(view: SettlementCameraView, animate = false) {
      this.cameraView = clampSettlementCamera(
        view,
        this.scale.width,
        this.scale.height,
        this.maximumZoom(),
        this.gridFrame?.registration?.cover,
      );
      if (!this.world) return;
      this.tweens.killTweensOf(this.world);
      const { zoom, offsetX, offsetY } = this.cameraView;
      this.resizeTouchZones(zoom);
      this.drawPlacementGuides(zoom);
      if (animate)
        this.tweens.add({
          targets: this.world,
          x: offsetX,
          y: offsetY,
          scaleX: zoom,
          scaleY: zoom,
          duration: 280,
          ease: 'Sine.InOut',
          onUpdate: () => this.drawPlacementGuides(this.world!.scaleX),
        });
      else this.world.setPosition(offsetX, offsetY).setScale(zoom);
      this.refreshHomeResolution();
    }

    private refreshHomeResolution() {
      const backingRatio = this.game.canvas.width / this.scale.width;
      for (const { art, asset, width } of this.responsiveHomes) {
        const delivery = homeTextureVariants[asset.url];
        if (!delivery || !art.scene || this.nativeArtFallbacks.has(asset.id)) continue;
        const key = nativeHomeTextureKey(asset.id);
        if (this.textures.exists(key)) {
          if (art.texture.key === key) continue;
          // Keep world geometry and normalized source registration. The hit callback
          // samples current texture pixels; its native rectangle must change with it.
          art.setTexture(key).setScale(width / this.textures.get(key).getSourceImage().width);
          if (art.input?.hitArea instanceof Phaser.Geom.Rectangle)
            art.input.hitArea.setSize(art.width, art.height);
        } else if (
          needsNativeHomeTexture(delivery.variant.width, width, this.cameraView.zoom, backingRatio)
        )
          this.homeUpgrades?.request(key, asset.url);
      }
    }

    private iso(
      gridX: number,
      gridY: number,
      tileWidth: number,
      tileHeight: number,
      frame = this.gridFrame,
    ): SettlementGroundProjection {
      return projectSettlementGround(
        gridX,
        gridY,
        this.scale.width,
        this.scale.height,
        tileWidth,
        tileHeight,
        frame,
      );
    }

    private gridFrame: SettlementGridFrame | undefined;
    private districtFrames = new Map<string, SettlementGridFrame>();

    private maximumZoom() {
      return this.gridFrame?.registration
        ? SETTLEMENT_REGISTERED_EDITOR_MAX_ZOOM
        : placement || editing
          ? 4
          : 3;
    }

    private districtForBounds(bounds?: SettlementGridBounds) {
      return (
        bounds &&
        snapshot.districts.find(
          (district) =>
            district.bounds.minX === bounds.minX &&
            district.bounds.minY === bounds.minY &&
            district.bounds.maxX + 1 === bounds.maxX &&
            district.bounds.maxY + 1 === bounds.maxY,
        )
      );
    }

    private frameForBuilding(building: BuildingView) {
      const target =
        placement?.buildingId === building.id
          ? this.districtForBounds(placement.districtBounds)?.code
          : undefined;
      return this.districtFrames.get(target ?? building.districtCode) ?? this.gridFrame;
    }

    private initialWorldCamera(): SettlementCameraView {
      const registration = this.districtFrames.get('lakeside_outpost')?.registration;
      if (!registration) return initialSettlementCamera(this.scale.width, this.scale.height);
      const xs = registration.targetQuad.map((point) => point.x);
      // Balance the phone's horizontal crop around the actual main island. Never shrink
      // just the buildings/grid to fake a fit or expose empty space below the backdrop.
      return {
        zoom: 1,
        offsetX: this.scale.width * 0.5 - (Math.min(...xs) + Math.max(...xs)) * 0.5,
        offsetY: 0,
      };
    }

    private applyEditorFocus() {
      const buildings = [...this.buildingLayers.keys()].flatMap((id) => {
        const sprite = this.buildingSprites.get(id);
        const anchor = this.buildingAnchors.get(id);
        if (!sprite?.scene || !anchor) return [];
        const bounds = sprite.getBounds();
        return [
          {
            id,
            depth: anchor.y,
            bounds: {
              left: bounds.left,
              top: bounds.top,
              right: bounds.right,
              bottom: bounds.bottom,
            },
          },
        ];
      });
      for (const [id, style] of planSettlementEditorFocus(buildings, placement?.buildingId)) {
        this.buildingLayers.get(id)?.setAlpha(style.alpha).setDepth(style.depth);
      }
    }

    private ambientGridPoint(x: number, y: number): Point {
      const frame = this.districtFrames.get('lakeside_outpost') ?? this.gridFrame;
      return frame
        ? {
            x: frame.centerX + (x - 4) / frame.scale,
            y: frame.centerY + (y - 4) / frame.scale,
          }
        : { x, y };
    }

    private ambientIso(x: number, y: number, tileWidth: number, tileHeight: number) {
      const point = this.ambientGridPoint(x, y);
      return this.iso(
        point.x,
        point.y,
        tileWidth,
        tileHeight,
        this.districtFrames.get('lakeside_outpost'),
      );
    }

    private polygon(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      points: readonly Point[],
      fill: number,
      alpha = 1,
      stroke?: { alpha: number; color: number; width: number },
    ) {
      graphics.fillStyle(fill, alpha);
      graphics.fillPoints([...points], true);
      if (stroke) {
        graphics.lineStyle(stroke.width, stroke.color, stroke.alpha);
        graphics.strokePoints([...points], true);
      }
    }

    private diamond(centerX: number, centerY: number, width: number, depth: number): Point[] {
      return [
        { x: centerX, y: centerY - depth / 2 },
        { x: centerX + width / 2, y: centerY },
        { x: centerX, y: centerY + depth / 2 },
        { x: centerX - width / 2, y: centerY },
      ];
    }

    private drawIsoPrism(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      centerX: number,
      groundY: number,
      width: number,
      depth: number,
      height: number,
      top = materials.stoneTop,
      left = materials.stoneSide,
      right = materials.stoneDark,
    ) {
      const topPoints = this.diamond(centerX, groundY - height, width, depth);
      const [, topRight, topFront, topLeft] = topPoints;
      if (!topRight || !topFront || !topLeft) return;
      const bottomRight = { x: centerX + width / 2, y: groundY };
      const bottomFront = { x: centerX, y: groundY + depth / 2 };
      const bottomLeft = { x: centerX - width / 2, y: groundY };
      this.polygon(graphics, [topLeft, topFront, bottomFront, bottomLeft], left);
      this.polygon(graphics, [topFront, topRight, bottomRight, bottomFront], right);
      this.polygon(graphics, topPoints, top, 1, {
        alpha: 0.42,
        color: materials.stoneLight,
        width: 1,
      });
    }

    private drawWorld() {
      if (!this.ensureSceneArt(() => this.drawWorld())) return;
      const width = this.scale.width;
      const height = this.scale.height;
      // A hidden responsive host may briefly report zero size. Preserve its layers
      // until a real viewport returns, rather than projecting a degenerate world.
      if (width <= 0 || height <= 0) return;
      if (this.world)
        this.cameraView = {
          zoom: this.world.scaleX,
          offsetX: this.world.x,
          offsetY: this.world.y,
        };
      this.tweens.killAll();
      this.placementGuides = undefined;
      this.children.removeAll(true);
      this.clearGroundTextures();
      this.selectionLayers.clear();
      this.buildingAnchors.clear();
      this.buildingLayers.clear();
      this.buildingSprites.clear();
      this.responsiveHomes = [];
      this.touchZones = [];
      this.visualRevision = settlementSceneRevision(snapshot);
      // A broader authored ground footprint lets the district read as a city,
      // while every prop still shares the same perspective projection.
      const tileWidth = settlementTileWidth(height);
      const tileHeight = tileWidth / 2;
      const plan = createSettlementScenePlan(snapshot, options.qualityTier);
      // Geography owns the camera frame; moving a structure must visibly move it
      // relative to the world rather than recentering the city around that object.
      const occupiedPlots = plan.buildings.map((building) => this.buildingBounds(building));
      this.occupiedPlots = occupiedPlots;
      // Editing keeps the world clear; routes never preview or reserve unsaved placements.
      this.publicPaths =
        editing || placement
          ? []
          : planSettlementPublicPaths(
              snapshot.districts,
              plan.buildings.map((building) => ({
                districtCode: building.districtCode,
                bounds: this.buildingBounds(building),
              })),
            );
      const districtBounds = settlementFramingDistricts(
        snapshot.districts,
        plan.buildings.map((building) => building.districtCode),
        placement?.districtBounds,
      );
      this.groundDistricts = districtBounds;
      this.gridFrame = createSettlementWorldFrame(districtBounds, occupiedPlots, width, height);
      this.districtFrames.clear();
      for (const district of snapshot.districts) {
        const frame = createSettlementDistrictFrame(
          {
            code: district.code,
            bounds: {
              ...district.bounds,
              maxX: district.bounds.maxX + 1,
              maxY: district.bounds.maxY + 1,
            },
          },
          width,
          height,
        );
        if (frame) this.districtFrames.set(district.code, frame);
      }
      // Each island retains its own ground registration. Choosing another district
      // changes the inverse/edit camera, never the position of unrelated buildings.
      const activeDistrict =
        placement || editing ? this.districtForBounds(this.editorDistrict()) : undefined;
      this.gridFrame =
        this.districtFrames.get(activeDistrict?.code ?? 'lakeside_outpost') ?? this.gridFrame;
      if (
        placement &&
        this.placementCameraPending &&
        this.gridFrame &&
        this.placementSourceFootprint
      ) {
        this.cameraView = createSettlementPlacementCamera(
          this.gridFrame,
          this.placementSourceFootprint,
          width,
          height,
          placement?.unobscuredViewport,
        );
        this.placementCameraPending = false;
      } else if (
        editing &&
        this.placementCameraPending &&
        this.gridFrame &&
        this.editorDistrict()
      ) {
        this.cameraView = createSettlementEditorCamera(
          this.gridFrame,
          this.editorDistrict()!,
          width,
          height,
          placement?.unobscuredViewport,
        );
        this.placementCameraPending = false;
      }
      const world = this.add.container(0, 0);
      this.world = world;

      this.drawBackdrop(world, width, height);
      this.drawExpansionGround(world);
      this.drawIslandBridges(world);
      this.drawWater(world, width, height, plan);
      this.drawDistrictOverlays(world, tileWidth, tileHeight, plan);
      this.drawSettlementPaths(world, tileWidth, tileHeight, plan);
      this.drawCanalsAndBridges(world, tileWidth, tileHeight, plan);
      this.drawCivicDistricts(world, tileWidth, tileHeight, plan);
      const editBounds = editing ? this.editorDistrict() : placement?.districtBounds;
      if (editBounds) {
        this.placementGuides = this.add.graphics();
        world.add(this.placementGuides);
      }
      for (const building of [...plan.buildings].sort(compareSettlementGroundDepth)) {
        const buildingLayer = this.add.container(0, 0);
        this.buildingLayers.set(building.id, buildingLayer);
        world.add(buildingLayer);
        this.drawBuilding(
          buildingLayer,
          placement?.buildingId === building.id
            ? {
                ...building,
                gridX: placement.x,
                gridY: placement.y,
                orientation: placement.orientation ?? building.orientation,
                footprintWidth: placement.footprintWidth ?? building.footprintWidth,
                footprintHeight: placement.footprintHeight ?? building.footprintHeight,
              }
            : building,
          tileWidth,
          tileHeight,
          plan.capabilitiesByBuilding[building.id] ?? [],
        );
      }
      // Gardens and farms are player-owned building layers. Never add lookalike
      // plots at arbitrary positions; natural vegetation is part of the island art.
      this.drawMarketAndHarborProps(world, plan);
      this.drawBanners(world, tileWidth, tileHeight, plan);
      this.drawAmbientLife(world, tileWidth, tileHeight, plan);
      this.applyEditorFocus();
      world.sort('depth');
      this.setCameraView(this.cameraView);
    }

    private drawPlacementGuides(zoom: number) {
      const boundary = this.placementGuides;
      const bounds = editing ? this.editorDistrict() : placement?.districtBounds;
      if (!boundary || !bounds) return;
      boundary.clear();
      const tile = settlementTileWidth(this.scale.height);
      const project = (x: number, y: number) => this.iso(x, y, tile, tile / 2);
      const corners = (plot: SettlementGridBounds) => [
        project(plot.minX, plot.minY),
        project(plot.maxX, plot.minY),
        project(plot.maxX, plot.maxY),
        project(plot.minX, plot.maxY),
      ];
      const stroke = settlementGridGuideStroke(zoom);
      this.polygon(boundary, corners(bounds), materials.jade, 0.025);
      if (editing) {
        // Occupied plots stay readable without treating decorative sprite overhang as land.
        for (const building of snapshot.buildings) {
          if (building.id === placement?.buildingId) continue;
          const plot = this.buildingBounds(building);
          if (
            plot.minX >= bounds.minX &&
            plot.maxX <= bounds.maxX &&
            plot.minY >= bounds.minY &&
            plot.maxY <= bounds.maxY
          )
            this.polygon(boundary, corners(plot), materials.obsidian, 0.1);
        }
        const seam = (from: Point, to: Point) => {
          boundary.lineStyle(stroke.recess, materials.obsidian, 0.42);
          boundary.lineBetween(from.x, from.y, to.x, to.y);
          boundary.lineStyle(stroke.seam, materials.goldGlow, 0.55);
          boundary.lineBetween(from.x, from.y, to.x, to.y);
        };
        for (let x = bounds.minX + 1; x < bounds.maxX; x += 1)
          seam(project(x, bounds.minY), project(x, bounds.maxY));
        for (let y = bounds.minY + 1; y < bounds.maxY; y += 1)
          seam(project(bounds.minX, y), project(bounds.maxX, y));
      }
      // Draw the perimeter last so cell seams never obscure the exact unlocked edge.
      this.polygon(boundary, corners(bounds), materials.jade, 0, {
        color: materials.goldGlow,
        alpha: 0.85,
        width: stroke.boundary,
      });
    }

    private drawDistrictOverlays(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      tileWidth: number,
      tileHeight: number,
      plan: SettlementScenePlan,
    ) {
      if (this.textures.exists(environmentAssetManifest.settlement.id)) {
        return;
      }
      const districts = this.add.graphics();
      for (const { district, overlay } of plan.districts) {
        const centerX = (district.bounds.minX + district.bounds.maxX) / 2;
        const centerY = (district.bounds.minY + district.bounds.maxY) / 2;
        const point = this.iso(centerX, centerY, tileWidth, tileHeight);
        const gridWidth = district.bounds.maxX - district.bounds.minX + 1;
        const gridHeight = district.bounds.maxY - district.bounds.minY + 1;
        const footprintWidth = Math.min(tileWidth * gridWidth * 0.68, tileWidth * 4.8);
        const footprintDepth = Math.min(tileHeight * gridHeight * 0.68, tileHeight * 4.8);
        const unlockedColor =
          overlay.terrainTreatment === 'cultivated_islands'
            ? materials.foliageLight
            : overlay.terrainTreatment === 'workshop_terraces'
              ? materials.timberLight
              : overlay.terrainTreatment === 'paved_market_islands'
                ? materials.goldAged
                : overlay.terrainTreatment === 'monumental_civic_stone'
                  ? materials.jadeWeathered
                  : materials.waterLight;
        this.polygon(
          districts,
          this.diamond(point.x, point.y, footprintWidth, footprintDepth),
          district.unlocked ? unlockedColor : materials.deep,
          district.unlocked ? 0.07 : 0.18,
        );
        if (!district.unlocked) {
          for (let mark = 0; mark < 5; mark += 1) {
            const angle = (mark / 5) * Math.PI * 2;
            districts.fillStyle(materials.reed, 0.2);
            districts.fillEllipse(
              point.x + Math.cos(angle) * footprintWidth * 0.23,
              point.y + Math.sin(angle) * footprintDepth * 0.2,
              13 + (mark % 2) * 5,
              4 + (mark % 3),
            );
          }
        } else if (overlay.terrainTreatment === 'cultivated_islands') {
          districts.fillStyle(materials.foliageLight, 0.45);
          districts.fillEllipse(point.x, point.y, footprintWidth * 0.42, footprintDepth * 0.3);
        } else if (overlay.terrainTreatment === 'monumental_civic_stone') {
          districts.fillStyle(materials.goldAged, 0.24);
          districts.fillCircle(point.x, point.y, Math.max(5, footprintDepth * 0.12));
        }
      }
      world.add(districts);
    }

    private drawBackdrop(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      width: number,
      height: number,
    ) {
      const environment = environmentAssetManifest.settlement;
      if (this.textures.exists(environment.id)) {
        const cover = settlementIslandCover(width, height)!;
        const matte = this.add.image(cover.x, cover.y, environment.id).setOrigin(0);
        matte.setScale(cover.scale);
        world.add(matte);

        const grade = this.add.graphics();
        grade.fillGradientStyle(
          materials.haze,
          materials.haze,
          materials.waterDeep,
          materials.waterDeep,
          0.06,
          0.06,
          0.16,
          0.16,
        );
        grade.fillRect(0, 0, width, height);
        world.add(grade);
        return;
      }

      const sky = this.add.graphics();
      sky.fillGradientStyle(
        materials.skyHigh,
        materials.skyHigh,
        materials.skyLow,
        materials.skyLow,
        1,
      );
      sky.fillRect(0, 0, width, height * 0.42);
      sky.fillStyle(materials.goldGlow, 0.46);
      sky.fillCircle(width * 0.76, height * 0.105, Math.max(18, width * 0.035));
      sky.fillStyle(materials.haze, 0.2);
      sky.fillCircle(width * 0.76, height * 0.105, Math.max(32, width * 0.06));
      this.polygon(
        sky,
        [
          { x: 0, y: height * 0.31 },
          { x: width * 0.2, y: height * 0.14 },
          { x: width * 0.39, y: height * 0.31 },
        ],
        materials.mountainFar,
        0.62,
      );
      this.polygon(
        sky,
        [
          { x: width * 0.22, y: height * 0.31 },
          { x: width * 0.49, y: height * 0.1 },
          { x: width * 0.73, y: height * 0.31 },
        ],
        materials.mountainNear,
        0.58,
      );
      this.polygon(
        sky,
        [
          { x: width * 0.57, y: height * 0.31 },
          { x: width * 0.83, y: height * 0.17 },
          { x: width, y: height * 0.3 },
        ],
        materials.mountainFar,
        0.52,
      );
      world.add(sky);

      const atmosphere = this.add.graphics();
      atmosphere.fillGradientStyle(
        materials.haze,
        materials.haze,
        materials.waterMid,
        materials.waterMid,
        0.12,
        0.12,
        0.72,
        0.72,
      );
      atmosphere.fillRect(0, height * 0.2, width, height * 0.24);
      world.add(atmosphere);
    }

    private drawWater(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      width: number,
      height: number,
      plan: SettlementScenePlan,
    ) {
      const water = this.add.graphics();
      const illustratedEnvironment = this.textures.exists(environmentAssetManifest.settlement.id);
      if (!illustratedEnvironment) {
        water.fillGradientStyle(
          materials.waterMid,
          materials.waterMid,
          materials.waterDeep,
          materials.waterShade,
          1,
        );
        water.fillRect(0, height * 0.29, width, height * 0.71);
      }
      const glintCount = options.qualityTier === 'low' ? 7 : 17;
      for (let index = 0; index < glintCount; index += 1) {
        const y = height * (0.36 + ((index * 0.071) % 0.58));
        const x = ((index * 173 + plan.progression * 37) % Math.max(1, width - 90)) + 22;
        const glintWidth = 24 + ((index * 19) % 64);
        water.lineStyle(index % 4 === 0 ? 2 : 1, materials.waterLight, 0.16 + (index % 3) * 0.04);
        water.lineBetween(x, y, Math.min(width - 16, x + glintWidth), y - 2);
      }
      world.add(water);
    }

    private drawTerraces(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      width: number,
      height: number,
      plan: SettlementScenePlan,
    ) {
      if (this.textures.exists(environmentAssetManifest.settlement.id)) {
        return;
      }
      const terrain = this.add.graphics();
      this.drawIsoPrism(
        terrain,
        width * 0.5,
        height * 0.69,
        Math.min(width * 0.92, 920),
        Math.min(height * 0.48, 330),
        18,
        materials.earthLight,
        materials.stoneSide,
        materials.stoneDark,
      );
      this.polygon(
        terrain,
        this.diamond(
          width * 0.5,
          height * 0.65,
          Math.min(width * 0.82, 820),
          Math.min(height * 0.39, 275),
        ),
        materials.stoneTop,
        0.98,
        { alpha: 0.38, color: materials.stoneLight, width: 1.5 },
      );

      for (let index = 0; index < plan.terraceCount; index += 1) {
        const side = index % 2 === 0 ? -1 : 1;
        const rank = Math.floor(index / 2);
        const x = width * 0.5 + side * width * (0.25 + rank * 0.065);
        const y = height * (0.48 + rank * 0.095 + (index % 3) * 0.012);
        const terraceWidth = Math.min(width * (0.15 - rank * 0.008), 132);
        const terraceDepth = Math.min(height * 0.072, 48);
        this.drawIsoPrism(
          terrain,
          x,
          y,
          terraceWidth,
          terraceDepth,
          7 + rank * 2,
          rank % 2 === 0 ? materials.earthLight : materials.foliageDark,
          materials.timber,
          materials.earth,
        );
        terrain.lineStyle(1, materials.maizeLeaf, 0.7);
        for (let crop = -2; crop <= 2; crop += 1) {
          const cropX = x + crop * terraceWidth * 0.13;
          terrain.lineBetween(
            cropX - terraceWidth * 0.08,
            y - terraceDepth * 0.18,
            cropX + terraceWidth * 0.08,
            y + terraceDepth * 0.04,
          );
        }
        if (plan.progression >= 2 && index % 2 === 0) {
          terrain.fillStyle(materials.waterLight, 0.52);
          terrain.fillRect(x - 3, y - 2, 6, 17 + rank * 4);
          terrain.fillStyle(materials.cloud, 0.22);
          terrain.fillEllipse(x, y + 16 + rank * 4, 15, 5);
        }
      }
      for (let joint = 0; joint < 11; joint += 1) {
        terrain.lineStyle(1, materials.stoneSide, 0.18);
        terrain.lineBetween(
          width * (0.2 + joint * 0.055),
          height * (0.57 + (joint % 3) * 0.035),
          width * (0.43 + joint * 0.038),
          height * (0.68 + (joint % 3) * 0.035),
        );
      }
      world.add(terrain);
    }

    private drawCanalsAndBridges(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      tileWidth: number,
      tileHeight: number,
      plan: SettlementScenePlan,
    ) {
      if (this.textures.exists(environmentAssetManifest.settlement.id)) {
        return;
      }
      const canals = this.add.graphics();
      const canalBands = plan.progression >= 2 ? 3 : 2;
      const canalRoutes = [
        [
          [0.4, 4.2],
          [2.1, 4.5],
          [3.9, 4.35],
          [5.7, 4.8],
          [7.7, 5.15],
        ],
        [
          [1.1, 6.25],
          [2.8, 5.85],
          [4.6, 6.05],
          [6.4, 6.6],
          [7.6, 7.05],
        ],
        [
          [2.5, 7.75],
          [3.8, 7.2],
          [5.35, 7.45],
          [6.6, 8.05],
        ],
      ] as const;
      for (let index = 0; index < canalBands; index += 1) {
        const path = (canalRoutes[index] ?? canalRoutes[0]).map(([gridX, gridY]) =>
          this.iso(gridX, gridY, tileWidth, tileHeight),
        );
        const perspectiveWidth = Math.max(9, tileWidth * (0.15 + index * 0.018));
        canals.lineStyle(perspectiveWidth + 7, materials.stoneSide, 0.64);
        canals.strokePoints(path, false);
        canals.lineStyle(perspectiveWidth, materials.waterDeep, 0.94);
        canals.strokePoints(path, false);
        canals.lineStyle(Math.max(1.5, tileWidth * 0.018), materials.waterLight, 0.56);
        canals.strokePoints(
          path.map((point) => ({ x: point.x, y: point.y - 3 })),
          false,
        );
      }

      for (let index = 0; index < plan.bridgeCount; index += 1) {
        const route = canalRoutes[index % canalBands] ?? canalRoutes[0];
        const anchor = route[1 + (Math.floor(index / canalBands) % Math.max(1, route.length - 2))];
        const projected = this.iso(anchor[0], anchor[1], tileWidth, tileHeight);
        const x = projected.x;
        const y = projected.y;
        const bridgeWidth = Math.max(22, tileWidth * 0.55 * projected.groundScale);
        this.polygon(
          canals,
          this.diamond(x, y, bridgeWidth, Math.max(12, bridgeWidth * 0.3)),
          materials.stoneLight,
          1,
          { alpha: 0.55, color: materials.stoneDark, width: 1 },
        );
        for (let seam = -1; seam <= 1; seam += 1) {
          canals.lineStyle(1, materials.stoneSide, 0.45);
          canals.lineBetween(
            x + seam * bridgeWidth * 0.17,
            y - bridgeWidth * 0.12,
            x + seam * bridgeWidth * 0.17,
            y + bridgeWidth * 0.12,
          );
        }
      }
      world.add(canals);
    }

    private drawSettlementPaths(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      tileWidth: number,
      tileHeight: number,
      plan: SettlementScenePlan,
    ) {
      if (!plan.buildings.length || !this.textures.exists(settlementPathMaterial.id)) return;
      const source = this.textures
        .get(settlementPathMaterial.id)
        .getSourceImage() as HTMLImageElement;
      const stamps = settlementPathStamps(this.publicPaths);
      const sourceQuad: IslandQuad = [
        { x: 0, y: 0 },
        { x: source.width, y: 0 },
        { x: source.width, y: source.height },
        { x: 0, y: source.height },
      ];
      for (const district of snapshot.districts) {
        const frame = this.districtFrames.get(district.code);
        if (!frame) continue;
        const quads = stamps
          .filter((stamp) => stamp.districtCode === district.code)
          .map(
            (stamp) =>
              stamp.ground.map((p) =>
                this.iso(p.x, p.y, tileWidth, tileHeight, frame),
              ) as unknown as IslandQuad,
          );
        if (!quads.length) continue;
        const corners = quads.flat();
        const left = Math.min(...corners.map((p) => p.x));
        const top = Math.min(...corners.map((p) => p.y));
        const width = Math.max(...corners.map((p) => p.x)) - left;
        const height = Math.max(...corners.map((p) => p.y)) - top;
        const resolution = Math.min(3, 1022 / Math.max(width, height));
        const key = `settlement-public-paths-${district.code}`;
        const texture = this.textures.createCanvas(
          key,
          Math.ceil(width * resolution) + 2,
          Math.ceil(height * resolution) + 2,
        );
        if (!texture) continue;
        this.groundTextures.add(key);
        const context = texture.getContext();
        context.scale(resolution, resolution);
        context.translate(-left, -top);
        // Preserve the source material's own feathered alpha: extra fading made
        // these already small ground details unreadable on phone overviews.
        context.globalAlpha = 1;
        for (const quad of quads) drawSettlementGroundTexture(context, source, sourceQuad, quad, 1);
        texture.refresh();
        world.add(
          this.add
            .image(left, top, key)
            .setOrigin(0)
            .setScale(1 / resolution),
        );
      }
    }

    private drawCivicDistricts(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      tileWidth: number,
      tileHeight: number,
      plan: SettlementScenePlan,
    ) {
      if (this.textures.exists(environmentAssetManifest.settlement.id)) {
        return;
      }
      const civic = this.add.graphics();
      for (let index = 0; index < plan.civicStructureCount; index += 1) {
        const point = this.iso(
          0.65 + ((index * 2.17) % 7.2),
          1.15 + ((index * 1.43) % 6.9),
          tileWidth,
          tileHeight,
        );
        const width = tileWidth * (0.34 + (index % 2) * 0.07) * point.groundScale;
        const depth = tileHeight * 0.52 * point.groundScale;
        this.drawIsoPrism(civic, point.x, point.y, width, depth, 12 + (index % 3) * 5);
        this.drawIsoPrism(
          civic,
          point.x,
          point.y - 12,
          width * 0.58,
          depth * 0.7,
          12 + (index % 2) * 8,
          materials.plaster,
        );
        civic.fillStyle(materials.obsidian, 0.8);
        civic.fillRect(point.x - 2, point.y - 35, 4, 14);
        civic.lineStyle(2, materials.jadeWeathered, 0.8);
        civic.lineBetween(point.x - width * 0.3, point.y - 11, point.x + width * 0.3, point.y - 11);
      }
      world.add(civic);
    }

    private drawBuilding(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      building: BuildingView,
      tileWidth: number,
      tileHeight: number,
      capabilities: SettlementScenePlan['capabilitiesByBuilding'][string],
    ) {
      const manifest = proceduralBuildingManifest[building.type];
      const frame = this.frameForBuilding(building);
      const plot = projectSettlementFootprint(
        this.buildingBounds(building),
        this.scale.width,
        this.scale.height,
        tileWidth,
        tileHeight,
        frame,
      );
      const point = plot.anchor;
      world.setDepth(point.y);
      const tier = Math.min(building.visualTier, 3);
      const artworkLevel =
        building.state === 'constructing'
          ? (building.constructionTargetLevel ?? building.level)
          : building.level;
      const footprintWidth = plot.width * 0.88;
      const footprintDepth = plot.depth * 0.88;
      const artworkWidth =
        footprintWidth *
        settlementBuildingArtScale({ ...building, level: artworkLevel }, manifest.footprint) *
        settlementBuildingTierScale(tier, manifest.tierScale);
      const selected = building.id === selectedBuildingId;
      const construction =
        building.state === 'constructing'
          ? resolveConstructionStage(building.progress ?? 0)
          : undefined;
      const constructionArt =
        construction &&
        resolveSettlementConstructionArt(
          building.type,
          construction.stage,
          artworkLevel,
          building.orientation,
        );
      const hasFittedContact =
        construction?.stage !== 'finishing' &&
        constructionArt &&
        constructionContactArt[constructionArt.id];
      const graphics = this.add.graphics();
      this.buildingAnchors.set(building.id, point);
      if (building.state !== 'constructing' || (building.progress ?? 0) >= 0.15)
        this.drawOwnedWaterFoundation(world, building, frame, tileWidth, tileHeight);
      if (!hasFittedContact) {
        graphics.fillStyle(materials.deep, 0.24);
        graphics.fillEllipse(
          point.x + 2,
          point.y - footprintDepth * 0.24,
          footprintWidth,
          footprintDepth * 0.55,
        );
      }
      world.add(graphics);
      const selection = this.add.graphics();
      const isPlacement = placement?.buildingId === building.id;
      if (isPlacement) {
        const bounds = this.buildingBounds(building);
        this.polygon(
          selection,
          [
            this.iso(bounds.minX, bounds.minY, tileWidth, tileHeight, frame),
            this.iso(bounds.maxX, bounds.minY, tileWidth, tileHeight, frame),
            this.iso(bounds.maxX, bounds.maxY, tileWidth, tileHeight, frame),
            this.iso(bounds.minX, bounds.maxY, tileWidth, tileHeight, frame),
          ],
          placement!.valid ? materials.jade : 0xd75a4a,
          0.12,
          {
            alpha: 1,
            color: placement!.valid ? materials.jade : 0xd75a4a,
            width: 2,
          },
        );
        // The projected heading supplements the independently rendered facade.
        // Upright artwork is never rotated in screen space.
        const layout = planSettlementConstructionLayout(bounds, building.orientation, 0);
        if (layout) {
          const cx = (bounds.minX + bounds.maxX) / 2;
          const cy = (bounds.minY + bounds.maxY) / 2;
          const { x: dx, y: dy } = layout.entranceHeading;
          const mark = (along: number, across: number) =>
            this.iso(
              cx + (dx * along - dy * across) * (bounds.maxX - bounds.minX),
              cy + (dy * along + dx * across) * (bounds.maxY - bounds.minY),
              tileWidth,
              tileHeight,
              frame,
            );
          this.polygon(
            selection,
            [mark(0.46, 0), mark(0.3, -0.12), mark(0.3, 0.12)],
            materials.goldGlow,
            1,
          );
        }
      }
      if (!isPlacement) {
        // A narrow contact arc preserves selection readability without
        // painting a large geometric overlay across the illustrated ground.
        selection.lineStyle(2, materials.goldGlow, 0.9);
        selection.strokeEllipse(
          point.x,
          point.y - footprintDepth * 0.2,
          footprintWidth * 0.9,
          footprintDepth * 0.4,
        );
        selection.lineStyle(1, materials.jade, 0.9);
        selection.strokeEllipse(
          point.x,
          point.y - footprintDepth * 0.2,
          footprintWidth * 0.83,
          footprintDepth * 0.32,
        );
      }
      selection.setVisible(selected || isPlacement);
      this.selectionLayers.set(building.id, selection);
      world.add(selection);

      let buildingArt: InstanceType<typeof Phaser.GameObjects.Image> | undefined;
      if (!construction || construction.stage === 'activation') {
        const sprite = resolveSettlementBuildingSprite(
          building.type,
          'completed',
          artworkLevel,
          building.orientation,
        );
        if (sprite && this.textures.exists(sprite.id)) {
          buildingArt = this.drawBuildingSprite(
            world,
            sprite,
            point,
            artworkWidth,
            building.type,
            artworkLevel,
            building.orientation,
          );
        } else {
          this.drawCompletedBuilding(
            graphics,
            manifest,
            point,
            footprintWidth,
            footprintDepth,
            tier,
          );
        }
        const details = this.add.graphics();
        this.drawBranchDetails(details, building, point, footprintWidth, footprintDepth);
        this.drawCapabilityCues(selection, point, footprintWidth, footprintDepth, capabilities);
        world.add(details);
        if (construction?.stage === 'activation') {
          this.drawLivingActivation(world, graphics, point, footprintWidth, footprintDepth);
        }
      } else {
        const state = manifest.constructionStates.find(
          (candidate) => candidate.stage === construction.stage,
        );
        const sprite =
          construction.stage === 'structure'
            ? resolveSettlementBuildingSprite(
                building.type,
                'structure',
                artworkLevel,
                building.orientation,
              )
            : undefined;
        if (sprite && this.textures.exists(sprite.id)) {
          // Structure art retains its own source registration, not the incoming monument's
          // different transparent padding. Ground occupancy and tier growth are unchanged.
          const structureWidth =
            footprintWidth *
            registeredArtworkScale(building.type, artworkLevel, 'structure', building.orientation) *
            settlementBuildingTierScale(tier, manifest.tierScale);
          buildingArt = this.drawBuildingSprite(
            world,
            sprite,
            point,
            structureWidth,
            building.type,
            artworkLevel,
            building.orientation,
          );
        } else if (state) {
          buildingArt = this.drawConstructionStage(
            world,
            graphics,
            manifest,
            point,
            footprintWidth,
            footprintDepth,
            artworkWidth,
            state,
            artworkLevel,
            building,
            tileWidth,
            tileHeight,
          );
        }
      }

      if (buildingArt) this.buildingSprites.set(building.id, buildingArt);
      const target =
        buildingArt && buildingArt.displayWidth >= 48 && buildingArt.displayHeight >= 48
          ? buildingArt.setInteractive({
              hitArea: new Phaser.Geom.Rectangle(0, 0, buildingArt.width, buildingArt.height),
              hitAreaCallback: (_area: unknown, x: number, y: number) =>
                isSettlementSpriteHit(
                  {
                    x,
                    y,
                    width: buildingArt.width,
                    height: buildingArt.height,
                    cropBottom: this.cropHeights.get(buildingArt),
                  },
                  (px, py) =>
                    this.textures.getPixelAlpha(
                      px,
                      py,
                      buildingArt.texture.key,
                      buildingArt.frame.name,
                    ),
                ),
              useHandCursor: true,
            })
          : this.add
              .zone(
                point.x,
                point.y - footprintDepth * 0.8,
                Math.max(48, footprintWidth),
                Math.max(48, footprintDepth * 2.4),
              )
              .setInteractive({ useHandCursor: true });
      target.on('pointerup', (pointer: InstanceType<typeof Phaser.Input.Pointer>) => {
        if ((placement && !editing) || (editing && building.state === 'constructing')) return;
        if (this.gestureTravel < 8 && pointer.getDistance() < 9 && this.pointers.size <= 1)
          options.onBuildingSelected(building.id);
      });
      target.on('pointerdown', (pointer: InstanceType<typeof Phaser.Input.Pointer>) => {
        if (!placement || placement.buildingId !== building.id || this.pointers.size > 0) return;
        const pointerPoint = this.pointerInWorld(pointer);
        const origin = this.iso(placement.x, placement.y, tileWidth, tileHeight, frame);
        this.lastPlacementTile = { x: placement.x, y: placement.y };
        this.placementDrag = {
          pointerId: pointer.id,
          offset: { x: pointerPoint.x - origin.x, y: pointerPoint.y - origin.y },
        };
      });
      if (!buildingArt || target !== buildingArt) world.add(target);
      if (target instanceof Phaser.GameObjects.Zone) {
        this.touchZones.push({
          zone: target,
          width: Math.max(8, footprintWidth),
          height: Math.max(8, footprintDepth * 2.4),
        });
      }
    }

    private clearGroundTextures() {
      for (const key of this.groundTextures) this.textures.remove(key);
      this.groundTextures.clear();
    }

    private drawExpansionGround(world: InstanceType<typeof Phaser.GameObjects.Container>) {
      for (const district of snapshot.districts) {
        const registration = this.districtFrames.get(district.code)?.registration;
        if (!registration) continue;
        const ground = settlementExpansionGround(
          district.code,
          district.unlocked,
          registration.targetQuad,
        );
        if (!ground) continue;
        const image = this.textures.get(ground.asset.id).getSourceImage() as HTMLImageElement;
        const left = Math.min(...ground.target.map((p) => p.x)),
          top = Math.min(...ground.target.map((p) => p.y));
        const width = Math.max(...ground.target.map((p) => p.x)) - left;
        const height = Math.max(...ground.target.map((p) => p.y)) - top;
        const resolution = Math.min(3, 2048 / Math.max(width, height));
        const key = `owned-island-${district.code}`;
        const texture = this.textures.createCanvas(
          key,
          Math.ceil(width * resolution) + 2,
          Math.ceil(height * resolution) + 2,
        );
        if (!texture) continue;
        this.groundTextures.add(key);
        const context = texture.getContext();
        context.scale(resolution, resolution);
        context.translate(-left, -top);
        context.beginPath();
        context.rect(left, top, width, height);
        const cover = registration.cover;
        settlementPrimaryPreservationOutline.forEach((point, index) => {
          const x = cover.x + point.x * cover.scale,
            y = cover.y + point.y * cover.scale;
          if (index === 0) context.moveTo(x, y);
          else context.lineTo(x, y);
        });
        context.closePath();
        context.clip('evenodd');
        // Match the authored basin's softer atmospheric material response. This affects
        // only the replaceable expansion layer, never the original environment pixels.
        context.filter = 'saturate(0.85) contrast(0.88) brightness(0.98)';
        drawSettlementGroundTexture(context, image, ground.source, ground.target);
        context.filter = 'none';
        // Reach zero opacity at the protected edge, unlike a blurred fill's half-opacity seam.
        // Erasure stays on the expansion texture; original basin pixels never change.
        drawSettlementShoreFeather(
          context,
          settlementPrimaryPreservationOutline.map(({ x, y }) => ({
            x: cover.x + x * cover.scale,
            y: cover.y + y * cover.scale,
          })),
          16 * cover.scale,
        );
        texture.refresh();
        world.add(
          this.add
            .image(left, top, key)
            .setOrigin(0)
            .setScale(1 / resolution),
        );
      }
    }

    private drawIslandBridges(world: InstanceType<typeof Phaser.GameObjects.Container>) {
      const asset = settlementBridgeDeckDelivery(snapshot.districts);
      if (!asset) return;
      if (!this.textures.exists(asset.id) || !this.textures.exists(settlementPathMaterial.id))
        return;
      const image = this.textures.get(asset.id).getSourceImage() as HTMLImageElement;
      const stone = this.textures
        .get(settlementPathMaterial.id)
        .getSourceImage() as HTMLImageElement;
      const stoneSource: IslandQuad = [
        { x: 0, y: 0 },
        { x: stone.width, y: 0 },
        { x: stone.width, y: stone.height },
        { x: 0, y: stone.height },
      ];
      const postArt = settlementBridgePostManifest;
      const postImage = this.textures.exists(postArt.id)
        ? (this.textures.get(postArt.id).getSourceImage() as HTMLImageElement)
        : undefined;
      for (const district of snapshot.districts) {
        const cover = this.districtFrames.get(district.code)?.registration?.cover;
        if (!cover) continue;
        const decision = createSettlementIslandBridge(
          district.code,
          district.unlocked,
          cover,
          SETTLEMENT_BRIDGE_CONTACT_IDENTITY,
        );
        if (decision.status !== 'ready') continue;
        const material = planSettlementBridgeMaterial(decision.bridge);
        if (!material.timber.length) continue;
        const structure = planSettlementBridgeStructure(decision.bridge);
        const quad = decision.bridge.deckQuad;
        // Include the whole upright raster so its cap and alpha fringe are never clipped.
        // This changes texture bounds only, never the registered ground footprint.
        const points = [...quad];
        if (postImage)
          for (const post of [...structure.far.posts, ...structure.near.posts]) {
            const size = post.height / postArt.paintedHeight;
            const x = post.base.x - postArt.anchor.x * size;
            const y = post.base.y - postArt.anchor.y * size;
            points.push({ x, y }, { x: x + size, y: y + size });
          }
        const left = Math.min(...points.map((point) => point.x));
        const top = Math.min(...points.map((point) => point.y));
        const width = Math.max(...points.map((point) => point.x)) - left;
        const height = Math.max(...points.map((point) => point.y)) - top;
        const resolution = Math.min(4, 1022 / Math.max(width, height));
        const key = `island-bridge-${district.code}`;
        const texture = this.textures.createCanvas(
          key,
          Math.ceil(width * resolution) + 2,
          Math.ceil(height * resolution) + 2,
        );
        if (!texture) continue;
        this.groundTextures.add(key);
        const context = texture.getContext();
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';
        context.scale(resolution, resolution);
        context.translate(-left, -top);
        const drawSide = (side: SettlementBridgeSide) => {
          if (!postImage) return;
          for (const rail of side.rails)
            drawSettlementGroundTexture(
              context,
              postImage,
              settlementBridgeRailSource,
              rail.quad,
              1,
            );
          // No rotation, shear or independent X/Y scale on upright posts.
          for (const post of side.posts) {
            const size = post.height / postArt.paintedHeight;
            context.drawImage(
              postImage,
              post.base.x - postArt.anchor.x * size,
              post.base.y - postArt.anchor.y * size,
              size,
              size,
            );
          }
        };
        drawSide(structure.far);
        // Assemble one continuous strip before projecting it. Separate angled clips
        // would introduce antialiased hairline cracks between otherwise touching slices.
        const strip =
          asset.id === settlementGardenBridgeStripId && !this.nativeArtFallbacks.has(asset.id)
            ? rehydrateSettlementBridgeStrip(image)
            : assembleSettlementBridgeStrip(image, material);
        if (!strip) continue;
        const stripQuad: IslandQuad = [
          { x: 0, y: 0 },
          { x: strip.width, y: 0 },
          { x: strip.width, y: strip.height },
          { x: 0, y: strip.height },
        ];
        drawSettlementGroundTexture(context, strip, stripQuad, quad, 1);
        // Small square material patches soften wood/shore contact without extending
        // the reviewed deck footprint or pretending that a 2D plate proves elevation.
        for (const landing of material.landings)
          drawSettlementGroundTexture(context, stone, stoneSource, landing, 1);
        drawSide(structure.near);
        texture.refresh();
        world.add(
          this.add
            .image(left, top, key)
            .setOrigin(0)
            .setScale(1 / resolution),
        );
      }
    }

    private drawOwnedWaterFoundation(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      building: BuildingView,
      frame: SettlementGridFrame | undefined,
      tileWidth: number,
      tileHeight: number,
    ) {
      if (!supportsSettlementWaterFoundation(building.type)) return;
      const bounds = this.buildingBounds(building);
      // Ground-only raster: its four corners obey the exact legal plot projection.
      // Upright building art is never sheared. No foundation outside an owned building.
      const quad: IslandQuad = [
        this.iso(bounds.minX, bounds.minY, tileWidth, tileHeight, frame),
        this.iso(bounds.maxX, bounds.minY, tileWidth, tileHeight, frame),
        this.iso(bounds.maxX, bounds.maxY, tileWidth, tileHeight, frame),
        this.iso(bounds.minX, bounds.maxY, tileWidth, tileHeight, frame),
      ];
      const left = Math.min(...quad.map((p) => p.x)),
        top = Math.min(...quad.map((p) => p.y));
      const width = Math.max(...quad.map((p) => p.x)) - left;
      const height = Math.max(...quad.map((p) => p.y)) - top;
      if (![left, top, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return;
      const key = `owned-water-${building.id}`;
      if (this.groundTextures.has(key)) this.textures.remove(key);
      // Allocate only on scene/placement changes; bounded resolution, never per animation frame.
      const resolution = Math.min(8, 1024 / Math.max(width, height));
      const texture = this.textures.createCanvas(
        key,
        Math.ceil(width * resolution) + 2,
        Math.ceil(height * resolution) + 2,
      );
      if (!texture) return;
      this.groundTextures.add(key);
      const context = texture.getContext();
      context.scale(resolution, resolution);
      context.translate(-left, -top);
      const source = this.textures
        .get(settlementWaterFoundationManifest.id)
        .getSourceImage() as HTMLImageElement;
      if (!drawSettlementWaterFoundation(context, source, building.type, quad)) return;
      texture.refresh();
      world.add(
        this.add
          .image(left, top, key)
          .setOrigin(0)
          .setScale(1 / resolution),
      );
    }

    private drawBuildingSprite(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      asset: SettlementBuildingSpriteAsset,
      point: SettlementGroundProjection,
      width: number,
      type: BuildingView['type'],
      level = 1,
      orientation = 0,
    ) {
      // Uniform upright silhouette size may include bounded authored overhang;
      // the server-owned ground plot and foot anchor remain unchanged.
      const anchor = settlementBuildingGroundAnchor(type, asset.stage, level, orientation);
      const nativeKey = nativeHomeTextureKey(asset.id);
      const textureKey = this.textures.exists(nativeKey) ? nativeKey : asset.id;
      const art = this.add
        .image(point.x, point.y, textureKey)
        .setOrigin(anchor.x, anchor.y)
        .setScale(width / this.textures.get(textureKey).getSourceImage().width)
        .setAlpha(0.9 + Math.min(0.1, Math.max(0, point.depthScale - 0.7) * 0.34));
      if (homeTextureVariants[asset.url]) this.responsiveHomes.push({ art, asset, width });
      if (supportsSettlementWaterFoundation(type)) {
        const source = this.textures.get(asset.id).getSourceImage();
        // The owned basin supplies water. Exclude the old sprite's detached lower-water tail.
        const cropBottom = Math.min(source.height, Math.ceil(source.height * anchor.y) + 4);
        this.cropHeights.set(art, cropBottom);
        art.setCrop(0, 0, source.width, cropBottom);
      }
      world.add(art);
      return art;
    }

    private drawCapabilityCues(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      entries: SettlementScenePlan['capabilitiesByBuilding'][string],
    ) {
      entries.slice(0, 2).forEach(({ overlay }, index) => {
        const cueColor =
          overlay.cue === 'water'
            ? materials.waterLight
            : overlay.cue === 'knowledge'
              ? materials.jadeWeathered
              : overlay.cue === 'route'
                ? materials.textileRust
                : overlay.cue === 'craft'
                  ? materials.obsidianSheen
                  : overlay.cue === 'garden'
                    ? materials.foliageLight
                    : overlay.cue === 'civic'
                      ? materials.goldAged
                      : materials.goldGlow;
        const x = point.x + width * (0.34 - index * 0.18);
        const groundY = point.y + depth * (0.08 + index * 0.035);
        const mastHeight = Math.max(8, depth * 0.34);
        graphics.lineStyle(1.4, materials.timber, 0.92);
        graphics.lineBetween(x, groundY, x, groundY - mastHeight);
        graphics.fillStyle(materials.stoneLight, 0.72);
        graphics.fillEllipse(x, groundY + 1, 6, 2.4);
        graphics.fillStyle(cueColor, 0.88);
        graphics.fillTriangle(
          x + 1,
          groundY - mastHeight,
          x + Math.max(6, width * 0.085),
          groundY - mastHeight + Math.max(3, depth * 0.11),
          x + 1,
          groundY - mastHeight + Math.max(6, depth * 0.21),
        );
      });
    }

    private drawCompletedBuilding(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      manifest: ProceduralBuildingAsset,
      point: Point,
      width: number,
      depth: number,
      tier: number,
    ) {
      if (manifest.silhouette === 'temple') {
        this.drawSolarShrine(graphics, point, width, depth, tier);
      } else if (manifest.silhouette === 'courtyard') {
        this.drawCourtyard(graphics, point, width, depth, tier, manifest.activity);
      } else if (manifest.silhouette === 'garden') {
        this.drawGarden(graphics, point, width, depth, tier, manifest.activity);
      } else if (manifest.silhouette === 'dock') {
        this.drawDock(graphics, point, width, depth, tier);
      } else if (manifest.silhouette === 'archive') {
        this.drawArchive(graphics, point, width, depth, tier);
      } else {
        this.drawWorkshop(graphics, point, width, depth, tier, manifest.activity);
      }
    }

    private drawSolarShrine(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      tier: number,
    ) {
      const stages = 3 + tier;
      let rise = 0;
      for (let stage = 0; stage < stages; stage += 1) {
        const ratio = 1 - stage * 0.105;
        const stageHeight = Math.max(5, depth * 0.19);
        this.drawIsoPrism(
          graphics,
          point.x,
          point.y - rise,
          width * ratio,
          depth * ratio,
          stageHeight,
          stage % 2 === 0 ? materials.stoneLight : materials.stoneTop,
        );
        rise += stageHeight * 0.72;
      }
      const sanctuaryWidth = width * (tier >= 2 ? 0.38 : 0.32);
      const sanctuaryDepth = depth * 0.55;
      this.drawIsoPrism(
        graphics,
        point.x,
        point.y - rise,
        sanctuaryWidth,
        sanctuaryDepth,
        depth * (0.72 + tier * 0.08),
        materials.plaster,
      );
      graphics.fillStyle(materials.obsidian, 0.92);
      graphics.fillRect(
        point.x - sanctuaryWidth * 0.105,
        point.y - rise - depth * (0.66 + tier * 0.08),
        sanctuaryWidth * 0.21,
        depth * 0.43,
      );
      graphics.lineStyle(2, materials.jadeWeathered, 0.88);
      graphics.strokeRect(
        point.x - sanctuaryWidth * 0.13,
        point.y - rise - depth * (0.7 + tier * 0.08),
        sanctuaryWidth * 0.26,
        depth * 0.49,
      );

      const stairTop = point.y - rise * 0.55;
      for (let step = 0; step < 6 + tier; step += 1) {
        const ratio = step / (6 + tier);
        const stepWidth = width * (0.18 + ratio * 0.18);
        const y = stairTop + ratio * (rise + depth * 0.26);
        graphics.lineStyle(Math.max(1, depth * 0.055), materials.stoneLight, 0.94);
        graphics.lineBetween(point.x - stepWidth, y, point.x + stepWidth, y);
      }
      graphics.lineStyle(2, materials.goldAged, 0.82);
      graphics.lineBetween(
        point.x - width * 0.07,
        point.y - rise,
        point.x - width * 0.31,
        point.y + depth * 0.15,
      );
      graphics.lineBetween(
        point.x + width * 0.07,
        point.y - rise,
        point.x + width * 0.31,
        point.y + depth * 0.15,
      );

      const diskY = point.y - rise - depth * (0.92 + tier * 0.08);
      const diskRadius = Math.max(6, depth * (0.15 + tier * 0.012));
      graphics.fillStyle(materials.goldGlow, 0.96);
      graphics.fillCircle(point.x, diskY, diskRadius);
      graphics.lineStyle(1.5, materials.goldAged, 0.92);
      graphics.strokeCircle(point.x, diskY, diskRadius * 1.32);
      const rayCount = tier >= 2 ? 16 : 10;
      for (let ray = 0; ray < rayCount; ray += 1) {
        const angle = (Math.PI * 2 * ray) / rayCount;
        graphics.lineBetween(
          point.x + Math.cos(angle) * diskRadius * 1.48,
          diskY + Math.sin(angle) * diskRadius * 1.48,
          point.x + Math.cos(angle) * diskRadius * 2.05,
          diskY + Math.sin(angle) * diskRadius * 2.05,
        );
      }
      if (tier >= 2) {
        graphics.lineStyle(3, materials.waterLight, 0.72);
        graphics.lineBetween(
          point.x - width * 0.31,
          point.y - rise * 0.42,
          point.x - width * 0.31,
          point.y + depth * 0.18,
        );
        graphics.lineBetween(
          point.x + width * 0.31,
          point.y - rise * 0.42,
          point.x + width * 0.31,
          point.y + depth * 0.18,
        );
      }
    }

    private drawCourtyard(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      tier: number,
      activity: string,
    ) {
      this.drawIsoPrism(graphics, point.x, point.y, width, depth, 6, materials.stoneTop);
      const houseCount = tier >= 2 ? 3 : 2;
      for (let index = 0; index < houseCount; index += 1) {
        const offsetX = (index - (houseCount - 1) / 2) * width * 0.34;
        const offsetY = index % 2 === 0 ? -depth * 0.18 : -depth * 0.04;
        const houseWidth = width * (tier >= 2 ? 0.27 : 0.34);
        this.drawIsoPrism(
          graphics,
          point.x + offsetX,
          point.y + offsetY - 5,
          houseWidth,
          depth * 0.42,
          depth * (0.42 + tier * 0.06),
          materials.plaster,
        );
        this.drawThatchRoof(
          graphics,
          point.x + offsetX,
          point.y + offsetY - depth * (0.5 + tier * 0.06),
          houseWidth * 1.12,
          depth * 0.42,
        );
        graphics.fillStyle(materials.obsidian, 0.88);
        graphics.fillRect(point.x + offsetX - 2, point.y + offsetY - depth * 0.36, 4, depth * 0.28);
      }
      graphics.fillStyle(materials.foliage, 0.9);
      graphics.fillCircle(point.x, point.y - depth * 0.02, Math.max(3, depth * 0.09));
      if (activity === 'market') {
        graphics.fillStyle(materials.textileRust, 0.94);
        graphics.fillTriangle(
          point.x - width * 0.24,
          point.y - depth * 0.32,
          point.x,
          point.y - depth * 0.56,
          point.x + width * 0.24,
          point.y - depth * 0.32,
        );
      }
    }

    private drawThatchRoof(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      centerX: number,
      centerY: number,
      width: number,
      depth: number,
    ) {
      this.polygon(
        graphics,
        [
          { x: centerX, y: centerY - depth * 0.56 },
          { x: centerX + width / 2, y: centerY },
          { x: centerX, y: centerY + depth * 0.38 },
          { x: centerX - width / 2, y: centerY },
        ],
        materials.thatch,
        1,
        { alpha: 0.6, color: materials.thatchLight, width: 1 },
      );
      graphics.lineStyle(1, materials.timber, 0.48);
      for (let rib = -2; rib <= 2; rib += 1) {
        graphics.lineBetween(
          centerX,
          centerY - depth * 0.5,
          centerX + rib * width * 0.095,
          centerY + depth * 0.28,
        );
      }
    }

    private drawGarden(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      tier: number,
      activity: string,
    ) {
      this.drawIsoPrism(graphics, point.x, point.y, width, depth, 5 + tier, materials.timber);
      const bedCount = tier >= 2 ? 5 : 3;
      for (let bed = 0; bed < bedCount; bed += 1) {
        const offsetX = (bed - (bedCount - 1) / 2) * (width / (bedCount + 0.5));
        const bedWidth = width / (bedCount + 1.1);
        this.polygon(
          graphics,
          this.diamond(point.x + offsetX, point.y - depth * 0.19, bedWidth, depth * 0.62),
          materials.earth,
          1,
          { alpha: 0.65, color: materials.timberLight, width: 1 },
        );
        const stalks = tier >= 2 ? 5 : 3;
        for (let stalk = 0; stalk < stalks; stalk += 1) {
          const y = point.y - depth * (0.38 - stalk * 0.12);
          graphics.lineStyle(
            1.4,
            activity === 'farm' ? materials.maizeLeaf : materials.foliage,
            0.95,
          );
          graphics.lineBetween(point.x + offsetX, y, point.x + offsetX - 2, y - 8 - tier);
          graphics.fillStyle(
            activity === 'farm' ? materials.maizeEar : materials.foliageLight,
            0.92,
          );
          graphics.fillEllipse(point.x + offsetX + 1, y - 7 - tier, 4, 6);
        }
      }
      graphics.lineStyle(2, materials.waterLight, 0.62);
      graphics.lineBetween(
        point.x - width * 0.42,
        point.y,
        point.x + width * 0.42,
        point.y - depth * 0.38,
      );
    }

    private drawWorkshop(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      tier: number,
      activity: string,
    ) {
      this.drawIsoPrism(graphics, point.x, point.y, width, depth, 7, materials.stoneTop);
      const wallTop =
        activity === 'obsidian'
          ? materials.stoneDark
          : activity === 'timber'
            ? materials.timberLight
            : materials.plaster;
      this.drawIsoPrism(
        graphics,
        point.x,
        point.y - 6,
        width * 0.66,
        depth * 0.68,
        depth * (0.62 + tier * 0.08),
        wallTop,
        activity === 'obsidian' ? materials.obsidianSheen : materials.stoneSide,
        activity === 'obsidian' ? materials.obsidian : materials.stoneDark,
      );
      this.drawThatchRoof(
        graphics,
        point.x,
        point.y - depth * (0.72 + tier * 0.08),
        width * 0.77,
        depth * 0.58,
      );
      graphics.fillStyle(materials.obsidian, 0.95);
      graphics.fillRect(point.x - width * 0.08, point.y - depth * 0.6, width * 0.16, depth * 0.43);

      if (activity === 'obsidian') {
        for (let shard = -1; shard <= 1; shard += 1) {
          graphics.fillStyle(shard === 0 ? materials.obsidianSheen : materials.obsidian, 0.96);
          graphics.fillTriangle(
            point.x + shard * width * 0.2 - 4,
            point.y - depth * 0.05,
            point.x + shard * width * 0.2,
            point.y - depth * (0.38 + Math.abs(shard) * 0.08),
            point.x + shard * width * 0.2 + 5,
            point.y - depth * 0.04,
          );
        }
        graphics.fillStyle(materials.goldAged, 0.7);
        graphics.fillCircle(
          point.x + width * 0.28,
          point.y - depth * 0.25,
          Math.max(2, depth * 0.055),
        );
      } else if (activity === 'timber') {
        graphics.lineStyle(4, materials.timber, 0.95);
        for (let log = 0; log < 3; log += 1) {
          graphics.lineBetween(
            point.x + width * 0.23,
            point.y - log * 4,
            point.x + width * 0.48,
            point.y - depth * 0.13 - log * 4,
          );
        }
      } else {
        for (let block = 0; block < 3; block += 1) {
          this.drawIsoPrism(
            graphics,
            point.x + width * (0.25 + block * 0.1),
            point.y - block * 4,
            width * 0.12,
            depth * 0.15,
            4 + block * 2,
          );
        }
      }
    }

    private drawArchive(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      tier: number,
    ) {
      this.drawIsoPrism(graphics, point.x, point.y, width, depth, 7, materials.stoneTop);
      this.drawIsoPrism(
        graphics,
        point.x,
        point.y - 7,
        width * 0.66,
        depth * 0.62,
        depth * (0.74 + tier * 0.1),
        materials.plaster,
      );
      this.drawIsoPrism(
        graphics,
        point.x,
        point.y - depth * (0.65 + tier * 0.08),
        width * 0.77,
        depth * 0.68,
        5,
        materials.stoneLight,
      );
      graphics.fillStyle(materials.obsidian, 0.9);
      graphics.fillRect(point.x - width * 0.08, point.y - depth * 0.7, width * 0.16, depth * 0.48);
      for (let tablet = -1; tablet <= 1; tablet += 1) {
        graphics.fillStyle(materials.jadeWeathered, 0.88);
        graphics.fillRect(
          point.x + tablet * width * 0.19 - 2,
          point.y - depth * (0.88 + tier * 0.08),
          4,
          depth * 0.34,
        );
      }
    }

    private drawDock(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
      tier: number,
    ) {
      this.polygon(
        graphics,
        this.diamond(point.x, point.y, width, Math.max(10, depth * 0.5)),
        materials.timberLight,
        1,
        { alpha: 0.7, color: materials.timber, width: 1 },
      );
      const plankCount = 5 + tier * 2;
      for (let plank = 0; plank < plankCount; plank += 1) {
        const ratio = plank / Math.max(1, plankCount - 1);
        const x = point.x - width * 0.38 + ratio * width * 0.76;
        graphics.lineStyle(1, materials.timber, 0.72);
        graphics.lineBetween(x, point.y - depth * 0.2, x, point.y + depth * 0.2);
      }
      graphics.lineStyle(3, materials.timber, 0.9);
      graphics.lineBetween(
        point.x - width * 0.37,
        point.y - depth * 0.4,
        point.x - width * 0.37,
        point.y + depth * 0.55,
      );
      graphics.lineBetween(
        point.x + width * 0.37,
        point.y - depth * 0.4,
        point.x + width * 0.37,
        point.y + depth * 0.55,
      );
      if (tier >= 2)
        this.drawThatchRoof(graphics, point.x, point.y - depth * 0.45, width * 0.34, depth * 0.5);
    }

    private drawBranchDetails(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      building: BuildingView,
      point: Point,
      width: number,
      depth: number,
    ) {
      const overlay = resolveBranchOverlay(building.type, building.branchCode);
      if (overlay?.silhouetteLayer === 'water_channel') {
        graphics.lineStyle(3, materials.waterLight, 0.78);
        graphics.lineBetween(
          point.x - width * 0.44,
          point.y - depth * 0.03,
          point.x + width * 0.44,
          point.y - depth * 0.03,
        );
        graphics.fillStyle(materials.stoneLight, 0.96);
        graphics.fillCircle(point.x, point.y - depth * 0.03, Math.max(3, depth * 0.09));
      } else if (overlay?.silhouetteLayer === 'archive_inlay') {
        graphics.lineStyle(2, materials.jadeWeathered, 0.9);
        for (let mark = -1; mark <= 1; mark += 1)
          graphics.strokeRect(
            point.x + mark * width * 0.18 - 3,
            point.y - depth * 1.05,
            6,
            depth * 0.24,
          );
      } else if (overlay?.silhouetteLayer === 'garden_bloom') {
        for (let bloom = 0; bloom < 5; bloom += 1) {
          graphics.fillStyle(bloom % 2 === 0 ? materials.textileRust : materials.maizeEar, 0.8);
          graphics.fillCircle(
            point.x - width * 0.32 + bloom * width * 0.16,
            point.y - depth * 0.06,
            2.2,
          );
        }
      } else if (overlay?.silhouetteLayer === 'canopy') {
        graphics.fillStyle(materials.textileRust, 0.88);
        graphics.fillTriangle(
          point.x - width * 0.36,
          point.y - depth * 0.58,
          point.x,
          point.y - depth * 0.84,
          point.x + width * 0.36,
          point.y - depth * 0.58,
        );
      } else if (overlay?.silhouetteLayer === 'craft') {
        graphics.fillStyle(materials.obsidianSheen, 0.95);
        graphics.fillCircle(
          point.x + width * 0.35,
          point.y - depth * 0.44,
          Math.max(3, depth * 0.1),
        );
      } else if (overlay?.silhouetteLayer === 'banner') {
        this.drawBanner(graphics, point.x + width * 0.35, point.y - depth * 0.8, 0.72);
      }
    }

    private drawConstructionStage(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      manifest: ProceduralBuildingAsset,
      point: SettlementGroundProjection,
      width: number,
      depth: number,
      completedArtworkWidth: number,
      state: ConstructionMaterialState,
      level: number,
      building: BuildingView,
      tileWidth: number,
      tileHeight: number,
    ) {
      const asset = resolveSettlementConstructionArt(
        manifest.type,
        state.stage,
        level,
        building.orientation,
      );
      if (!asset || !this.textures.exists(asset.id)) return undefined;
      const bounds = this.buildingBounds(building);
      const layout = planSettlementConstructionLayout(
        bounds,
        building.orientation,
        state.workerCount,
      );
      const project = (anchor: Point) =>
        this.iso(anchor.x, anchor.y, tileWidth, tileHeight, this.frameForBuilding(building));
      const drawContactArt = (detail: { id: string; url: string }, plot: SettlementGridBounds) => {
        const contactArt = constructionContactArt[detail.id];
        if (!contactArt || !this.textures.exists(detail.id)) return undefined;
        const delivery = homeTextureVariants[detail.url];
        const nativeKey = nativeHomeTextureKey(detail.id);
        const textureKey = delivery && this.textures.exists(nativeKey) ? nativeKey : detail.id;
        const image = this.textures.get(textureKey).getSourceImage();
        const isMaster =
          image.width === contactArt.imageSize[0] && image.height === contactArt.imageSize[1];
        const isVariant =
          delivery &&
          image.width === delivery.variant.width &&
          image.height === delivery.variant.height &&
          delivery.sourceWidth === contactArt.imageSize[0] &&
          delivery.sourceHeight === contactArt.imageSize[1];
        if (!isMaster && !isVariant) return undefined;
        const corners = [
          project({ x: plot.minX, y: plot.minY }),
          project({ x: plot.maxX, y: plot.minY }),
          project({ x: plot.maxX, y: plot.maxY }),
          project({ x: plot.minX, y: plot.maxY }),
        ];
        const fit = fitConstructionContact(contactArt, corners);
        if (!fit) return undefined;
        // Every registered worksite gets tight contact shading, including survey,
        // planting and foundation pads. A generic plot ellipse made them float.
        // The source raster retains its own directional cast shading.
        this.polygon(graphics, constructionContactPolygon(contactArt, fit), materials.deep, 0.16);
        const sprite = this.add
          .image(fit.x, fit.y, textureKey)
          .setOrigin(fit.originX, fit.originY)
          .setScale((fit.scale * contactArt.imageSize[0]) / image.width)
          .setDepth(fit.y);
        world.add(sprite);
        if (delivery)
          this.responsiveHomes.push({
            art: sprite,
            asset: detail,
            width: fit.scale * contactArt.imageSize[0],
          });
        return sprite;
      };
      const accessoryPlot = (anchor: Point, fraction: number) => ({
        minX: anchor.x - ((bounds.maxX - bounds.minX) * fraction) / 2,
        maxX: anchor.x + ((bounds.maxX - bounds.minX) * fraction) / 2,
        minY: anchor.y - ((bounds.maxY - bounds.minY) * fraction) / 2,
        maxY: anchor.y + ((bounds.maxY - bounds.minY) * fraction) / 2,
      });
      const completed =
        state.stage === 'finishing'
          ? resolveSettlementBuildingSprite(manifest.type, 'completed', level, building.orientation)
          : undefined;
      const art = completed
        ? this.drawBuildingSprite(
            world,
            completed,
            point,
            completedArtworkWidth,
            manifest.type,
            level,
            building.orientation,
          )
        : drawContactArt(asset, bounds);
      if (!art) return undefined;
      // Sort attachments using the same ground plane, including after a turn.
      art.setDepth(point.y - depth * 0.5);
      if (state.stage === 'finishing') {
        // Completed silhouette, but materials are still being installed; no activation VFX.
        art.setTint(0xd5c9ae);
        const materials = settlementDetailSpriteManifest.survey;
        if (layout && this.textures.exists(materials.id)) {
          drawContactArt(materials, accessoryPlot(layout.supplies, 0.24));
        }
        const scaffold = resolveSettlementConstructionAccessory(state.stage, building.orientation);
        const worksite = planSettlementFinishingWorksite(bounds, project);
        if (worksite && scaffold && this.textures.exists(scaffold.id)) {
          // Uprights keep uniform scale. A visible rust textile and ladder distinguish
          // finishing from the completed silhouette, even with motion disabled.
          drawContactArt(scaffold, accessoryPlot(worksite, 0.38));
        }
      }
      if (layout)
        layout.workers.forEach((anchor, index) =>
          this.drawResident(world, project(anchor), Math.max(7, Math.min(13, width * 0.12)), index),
        );
      world.sort('depth');
      return art;
    }

    private drawResident(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      point: Point,
      height: number,
      index: number,
    ) {
      const asset = settlementDetailSpriteManifest.resident;
      if (!this.textures.exists(asset.id)) return;
      const resident = this.add
        .image(point.x, point.y, asset.id, asset.staticFrame)
        .setOrigin(0.5, asset.groundAnchorY)
        .setDisplaySize(height, height)
        .setFlipX(index % 2 === 1)
        .setDepth(point.y);
      // The current authored sheet is not a validated walk cycle. Keep residents
      // planted on the ground instead of sliding a static pose across the city.
      world.add(resident);
    }

    private drawLivingActivation(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      point: Point,
      width: number,
      depth: number,
    ) {
      const motion = constructionMotionProfile(options.reducedMotion);
      graphics.lineStyle(3, materials.waterLight, 0.82);
      graphics.lineBetween(
        point.x - width * 0.42,
        point.y + depth * 0.15,
        point.x + width * 0.42,
        point.y + depth * 0.15,
      );
      for (let bloom = 0; bloom < 5; bloom += 1) {
        graphics.fillStyle(bloom % 2 === 0 ? materials.maizeEar : materials.textileRust, 0.92);
        graphics.fillCircle(
          point.x - width * 0.3 + bloom * width * 0.15,
          point.y + depth * 0.28,
          2.4,
        );
      }
      const glow = this.add.graphics();
      glow.fillStyle(materials.goldGlow, motion.activationGlowAlpha);
      glow.fillCircle(point.x, point.y - depth * 0.8, Math.max(8, depth * 0.24));
      world.add(glow);
      if (motion.animateActivation) {
        this.tweens.add({ targets: glow, alpha: 0.46, duration: 900, yoyo: true, repeat: -1 });
      }
    }

    private drawMarketAndHarborProps(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      plan: SettlementScenePlan,
    ) {
      // Market stalls belong to the owned market sprite, never unrelated city plots.
      const canoe = settlementDetailSpriteManifest.canoe;
      const cover = this.districtFrames.get('lakeside_outpost')?.registration?.cover;
      if (!cover || !this.textures.exists(canoe.id)) return;
      for (let index = 0; index < Math.min(plan.boatCount, 4); index += 1) {
        // Authored source-water lane between the foreground expansions. Camera-independent.
        const x = cover.x + (710 + index * 58) * cover.scale;
        const y = cover.y + (765 + (index % 2) * 65) * cover.scale;
        const width = 34 * cover.scale;
        const boat = this.add
          .image(x, y, canoe.id)
          .setOrigin(0.5, canoe.groundAnchorY)
          .setScale(width / this.textures.get(canoe.id).getSourceImage().width)
          .setDepth(y)
          .setFlipX(index % 2 === 1);
        world.add(boat);
        if (!options.reducedMotion && options.qualityTier !== 'low') {
          this.tweens.add({
            targets: boat,
            x: x + (index % 2 === 0 ? 13 : -13) * cover.scale,
            y: y + (index % 2 === 0 ? -5 : 5) * cover.scale,
            duration: 8400 + index * 600,
            ease: 'Sine.InOut',
            yoyo: true,
            repeat: -1,
          });
        }
      }
    }

    private ambientPointIfUnoccupied(x: number, y: number, tileWidth: number, tileHeight: number) {
      const grid = this.ambientGridPoint(x, y);
      if (!this.isLand(grid)) return undefined;
      if (
        this.occupiedPlots.some(
          (plot) =>
            grid.x >= plot.minX &&
            grid.x <= plot.maxX &&
            grid.y >= plot.minY &&
            grid.y <= plot.maxY,
        )
      )
        return undefined;
      const point = this.iso(
        grid.x,
        grid.y,
        tileWidth,
        tileHeight,
        this.districtFrames.get('lakeside_outpost'),
      );
      // Keep decorative props within the authored basin's land band.
      if (
        point.y < this.scale.height * 0.3 ||
        point.y > this.scale.height * 0.7 ||
        point.x < this.scale.width * 0.03 ||
        point.x > this.scale.width * 0.97
      )
        return undefined;
      return point;
    }

    private isLand(point: Point) {
      return this.groundDistricts.some(
        (bounds) =>
          point.x >= bounds.minX &&
          point.x < bounds.maxX &&
          point.y >= bounds.minY &&
          point.y < bounds.maxY,
      );
    }

    private drawBanners(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      tileWidth: number,
      tileHeight: number,
      plan: SettlementScenePlan,
    ) {
      const asset = settlementDetailSpriteManifest.banner;
      if (!this.textures.exists(asset.id)) return;
      for (let index = 0; index < plan.bannerCount; index += 1) {
        const point = this.ambientPointIfUnoccupied(
          0.5 + ((index * 1.42) % 7.7),
          0.85 + ((index * 2.06) % 7.15),
          tileWidth,
          tileHeight,
        );
        if (!point) continue;
        const height = Math.max(3, Math.min(29, tileWidth * 0.4 * point.depthScale));
        const banner = this.add
          .image(point.x, point.y, asset.id)
          .setOrigin(0.42, asset.groundAnchorY)
          .setScale(height / this.textures.get(asset.id).getSourceImage().height)
          .setDepth(point.y);
        world.add(banner);
        if (!options.reducedMotion && options.qualityTier !== 'low') {
          this.tweens.add({
            targets: banner,
            scaleX: banner.scaleX * 0.97,
            duration: 2300 + index * 90,
            ease: 'Sine.InOut',
            yoyo: true,
            repeat: -1,
          });
        }
      }
    }

    private drawBanner(
      graphics: InstanceType<typeof Phaser.GameObjects.Graphics>,
      x: number,
      y: number,
      scale: number,
    ) {
      graphics.lineStyle(2.2, materials.timber, 0.96);
      graphics.lineBetween(x, y, x, y - 26 * scale);
      this.polygon(
        graphics,
        [
          { x: x + 1, y: y - 25 * scale },
          { x: x + 13 * scale, y: y - 21 * scale },
          { x: x + 11 * scale, y: y - 8 * scale },
          { x: x + 1, y: y - 11 * scale },
        ],
        materials.textileIndigo,
        0.96,
        { alpha: 0.68, color: materials.goldAged, width: 1 },
      );
      graphics.lineStyle(1, materials.goldAged, 0.85);
      graphics.strokeCircle(x + 6.5 * scale, y - 16.5 * scale, 2.2 * scale);
    }

    private drawAmbientLife(
      world: InstanceType<typeof Phaser.GameObjects.Container>,
      tileWidth: number,
      tileHeight: number,
      plan: SettlementScenePlan,
    ) {
      const residents = planSettlementResidents(
        snapshot.districts.filter((district) => this.districtFrames.has(district.code)),
        plan.buildings.map((building) => ({
          districtCode: building.districtCode,
          bounds: this.buildingBounds(building),
        })),
        Math.min(plan.npcCount, plan.buildings.length * 3),
        settlementPathStamps(this.publicPaths)
          .filter((_, index) => index % 3 === 1)
          .map((stamp) => ({ districtCode: stamp.districtCode, ...stamp.center })),
      );
      for (const [index, grid] of residents.entries()) {
        const point = this.iso(
          grid.x,
          grid.y,
          tileWidth,
          tileHeight,
          this.districtFrames.get(grid.districtCode),
        );
        this.drawResident(
          world,
          point,
          Math.max(2, Math.min(12, tileWidth * 0.2 * point.depthScale)),
          index,
        );
      }

      if (plan.rippleCount > 0) {
        for (let index = 0; index < plan.rippleCount; index += 1) {
          const ripple = this.add.graphics();
          ripple.lineStyle(1, materials.waterLight, 0.3);
          ripple.strokeEllipse(
            28 + ((index * 137) % Math.max(50, this.scale.width - 56)),
            this.scale.height * (0.73 + (index % 4) * 0.06),
            20 + (index % 4) * 8,
            5 + (index % 3) * 2,
          );
          world.add(ripple);
          if (!options.reducedMotion)
            this.tweens.add({
              targets: ripple,
              alpha: 0.18,
              duration: 1700 + index * 80,
              yoyo: true,
              repeat: -1,
            });
        }
      }

      for (let index = 0; index < plan.birdCount; index += 1) {
        const bird = this.add.graphics();
        bird.lineStyle(1.5, materials.deep, 0.68);
        bird.lineBetween(0, 3, 5, 0);
        bird.lineBetween(5, 0, 10, 3);
        bird.setPosition(40 + index * 78, 46 + (index % 2) * 22);
        world.add(bird);
        if (!options.reducedMotion)
          this.tweens.add({
            targets: bird,
            x: bird.x + 45,
            duration: 4400 + index * 320,
            yoyo: true,
            repeat: -1,
          });
      }

      const workshop = plan.buildings.find(
        (building) =>
          (building.type === 'obsidian_workshop' || building.type === 'timber_workshop') &&
          building.state !== 'constructing' &&
          building.id !== placement?.buildingId,
      );
      if (workshop && options.qualityTier !== 'low' && !options.reducedMotion) {
        const anchor = this.iso(
          workshop.gridX,
          workshop.gridY,
          tileWidth,
          tileHeight,
          this.frameForBuilding(workshop),
        );
        const smoke = this.add.graphics();
        for (let puff = 0; puff < 5; puff += 1) {
          smoke.fillStyle(materials.smoke, 0.1 + puff * 0.025);
          smoke.fillCircle(
            anchor.x + puff * 2 * anchor.depthScale,
            anchor.y - (tileHeight * 1.3 + puff * 8) * anchor.depthScale,
            (4 + puff * 1.6) * anchor.depthScale,
          );
        }
        world.add(smoke);
        this.tweens.add({
          targets: smoke,
          y: -7,
          alpha: 0.45,
          duration: 2600,
          yoyo: true,
          repeat: -1,
        });
      }
    }
  }

  const scene = new SettlementScene();
  const game = new Phaser.Game({
    // The scene is composed from Canvas-compatible runtime layers. Pinning the
    // renderer avoids transient incomplete WebGL framebuffers while responsive
    // game routes are resized or unmounted on mobile browsers.
    type: Phaser.CANVAS,
    parent,
    width: Math.max(parent.clientWidth, 320),
    height: Math.max(parent.clientHeight, 420),
    transparent: true,
    backgroundColor: semanticColors.backgroundDeep,
    scene,
    render: { antialias: options.qualityTier !== 'low', pixelArt: false, roundPixels: true },
    scale: { mode: Phaser.Scale.RESIZE, autoCenter: Phaser.Scale.CENTER_BOTH },
    input: { activePointers: 2 },
  });

  return {
    update: (nextSnapshot, nextSelectedBuildingId) =>
      scene.redraw(nextSnapshot, nextSelectedBuildingId),
    setPlacement: (preview) => scene.setPlacement(preview),
    setEditing: (next) => scene.setEditing(next),
    commandCamera: (action) => scene.commandCamera(action),
    destroy: () => destroySettlementGame(game),
  };
}
